// src/pages/SuperAdminPanel.jsx
// ═══════════════════════════════════════════════════════════════════
// 👑 PANEL SUPER ADMIN — Gestión global de empresas, planes y audit
// Estilo oscuro/profesional — separado visualmente del Dashboard
// ═══════════════════════════════════════════════════════════════════

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { jwtDecode } from "jwt-decode";

// ── TABS ─────────────────────────────────────────────────────────
const TABS = [
  { id: "stats",    label: "📊 Overview",  },
  { id: "tenants",  label: "🏢 Empresas",  },
  { id: "usuarios", label: "👥 Usuarios",  },
  { id: "planes",   label: "📦 Planes",    },
  { id: "paises",   label: "🌍 Países",    },
  { id: "billing",  label: "💳 Billing",   },
  { id: "audit",    label: "🛡️ Audit Log", },
];

const ROLES_USUARIO = ['admin','supervisor','supervisor_pos','almacenero','visualizador','cajero'];
const ROLES_CONTABLES = ['contador','auxiliar_contable','supervisor_contable'];

const estadoBadgeSus = (estado) => ({
  activa:     { background:'#052e16', color:'#6ee7b7' },
  gracia:     { background:'#422006', color:'#fb923c' },
  suspendida: { background:'#7f1d1d', color:'#fca5a5' },
  cancelada:  { background:'#1e293b', color:'#475569' },
}[estado] || { background:'#1e293b', color:'#94a3b8' });

const estadoBadgeFac = (estado) => ({
  pendiente:  { background:'#422006', color:'#fb923c' },
  pagada:     { background:'#052e16', color:'#6ee7b7' },
  vencida:    { background:'#7f1d1d', color:'#fca5a5' },
  fallida:    { background:'#450a0a', color:'#fca5a5' },
  anulada:    { background:'#1e293b', color:'#475569' },
}[estado] || { background:'#1e293b', color:'#94a3b8' });

const estadoBadgePago = (estado) => ({
  aprobado:    { background:'#052e16', color:'#6ee7b7' },
  rechazado:   { background:'#7f1d1d', color:'#fca5a5' },
  reembolsado: { background:'#1e3a5f', color:'#93c5fd' },
}[estado] || { background:'#1e293b', color:'#94a3b8' });

// ════════════════════════════════════════════════════════════════════
const SuperAdminPanel = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");
  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);

  // Data
  const [stats, setStats]     = useState(null);
  const [tenants, setTenants] = useState([]);
  const [planes, setPlanes]   = useState([]);
  const [audit, setAudit]     = useState([]);
  const [totalAudit, setTotalAudit] = useState(0);
  const [paises, setPaises]   = useState([]);

  // Modales
  const [modalTenant, setModalTenant]   = useState(false);
  const [modalPlan, setModalPlan]       = useState(false);
  const [modalModulos, setModalModulos] = useState(null); // tenant seleccionado
  const [tenantDetalle, setTenantDetalle] = useState(null);
  const [modalPais, setModalPais]       = useState(false);
  const [paisEditando, setPaisEditando] = useState(null); // null = nuevo, objeto = editar

  // Modal editar tenant
  const [modalEditar, setModalEditar]         = useState(null); // tenant objeto o null
  const [formEditar, setFormEditar]           = useState({ nombre: "", plan_id: "", estado: "" });
  const [previewPlan, setPreviewPlan]         = useState(null);
  const [loadingPreview, setLoadingPreview]   = useState(false);
  const [confirmCambio, setConfirmCambio]     = useState(false);

  // Modales trial
  const [modalExtenderTrial, setModalExtenderTrial] = useState(null); // tenant
  const [diasExtra, setDiasExtra]                   = useState(15);
  const [modalConvertir, setModalConvertir]         = useState(null); // tenant
  const [planConvertir, setPlanConvertir]           = useState("");

  // Tab Billing
  const [billingStats, setBillingStats]   = useState(null);
  const [suscripciones, setSuscripciones] = useState([]);
  const [facturas, setFacturas]           = useState([]);
  const [pagos, setPagos]                 = useState([]);
  const [tasas, setTasas]                 = useState([]);
  const [modalTasa, setModalTasa]         = useState(null); // null | 'crear' | objeto tasa
  const [formTasa, setFormTasa]           = useState({ moneda_origen:"USD", moneda_destino:"DOP", tasa:"", fecha:"", fuente:"manual" });
  const [eliminandoTasa, setEliminandoTasa] = useState(null);
  const [generandoFactura, setGenerandoFactura] = useState(null); // tenant_id en proceso
  const [filtroPagoEstado, setFiltroPagoEstado] = useState("");
  const [filtroPagoFecha, setFiltroPagoFecha]   = useState("");
  const [billingSubTab, setBillingSubTab] = useState("suscripciones");
  const [filtroEstadoSus, setFiltroEstadoSus] = useState("");
  const [filtroEstadoFac, setFiltroEstadoFac] = useState("pendiente");
  const [filtroTenantFac, setFiltroTenantFac] = useState("");
  const [modalCrearSus, setModalCrearSus]     = useState(false);
  const [modalPagar, setModalPagar]           = useState(null); // factura objeto
  const [formSus, setFormSus] = useState({ tenant_id:"", plan_id:"", tipo_ciclo:"mensual", precio_acordado:"", descuento_pct:"0", moneda:"DOP", metodo_pago:"manual", notas:"" });
  const [formPago, setFormPago] = useState({ monto:"", moneda:"DOP", metodo:"manual", referencia_pago:"", cuenta_bancaria_id:"", notas:"" });

  // Modal editar suscripción (billing tab)
  const [modalEditarSus, setModalEditarSus] = useState(null); // suscripción objeto
  const [formEditarSus, setFormEditarSus]   = useState({ metodo_pago:"manual", precio_acordado:"", descuento_pct:"0", moneda:"DOP", tipo_ciclo:"mensual", notas:"" });
  // Suscripción asociada al tenant que se edita en modalEditar (para campo método de pago)
  const [suscripcionEditar, setSuscripcionEditar] = useState(null);

  // Add-ons del tenant en edición
  const [addonsTenant, setAddonsTenant]   = useState({ asignados: [], disponibles: [] });
  const [loadingAddons, setLoadingAddons] = useState(false);
  const [savingAddon, setSavingAddon]     = useState(null); // addon_codigo en proceso
  const [preciosAddon, setPreciosAddon]   = useState({}); // {[codigo]: precio_acordado editable}

  // Tab Usuarios
  const [usuarios, setUsuarios]         = useState([]);
  const [filtroTenant, setFiltroTenant] = useState("");
  const [filtroRol, setFiltroRol]       = useState("");
  const [filtroBuscar, setFiltroBuscar] = useState("");
  const [modalUsuario, setModalUsuario] = useState(false);   // crear
  const [modalEditarUser, setModalEditarUser] = useState(null); // usuario objeto
  const FORM_USER_DEFAULT = { nombre:"", email:"", password:"", rol:"admin", tenant_id:"", activo:true };
  const [formUser, setFormUser]         = useState(FORM_USER_DEFAULT);

  // Formularios
  const [formTenant, setFormTenant] = useState({ nombre: "", rnc: "", email: "", telefono: "", plan_id: "", es_trial: false, dias_trial: 15, nombre_admin: "", email_admin: "", password_admin: "" });
  const [formPlan, setFormPlan]     = useState({ nombre: "", descripcion: "", precio_mensual: "", modulos_incluidos: [] });
  const [planEditando, setPlanEditando] = useState(null); // null = crear, objeto = editar
  const [formPais, setFormPais]     = useState({
    codigo: "", nombre: "", nombre_fiscal: "",
    moneda_codigo: "USD", moneda_nombre: "", moneda_simbolo: "$", moneda_decimales: 2,
    zona_horaria: "America/Santo_Domingo", formato_fecha: "DD/MM/YYYY",
    separador_decimal: ".", separador_miles: ",",
    id_fiscal_empresa_label: "RUC", id_fiscal_empresa_regex: "", id_fiscal_empresa_mask: "",
    id_fiscal_persona_label: "Cédula", id_fiscal_persona_regex: "", id_fiscal_persona_mask: "",
    impuesto_label: "IVA", impuesto_tasa_default: "",
    sistema_comprobantes: "", comprobantes_activo: true,
    ente_regulador_nombre: "", ente_regulador_url: "",
    activo: true, orden: 99
  });
  const [saving, setSaving]         = useState(false);
  const [mensaje, setMensaje]       = useState(null);

  const ADDONS_PAGO = [
    { codigo: 'ecf',            nombre: 'e-CF Facturación Electrónica', icono: '📄', precio_ref: 30 },
    { codigo: 'crm',            nombre: 'CRM',                          icono: '🤝', precio_ref: 15 },
    { codigo: 'multi_empresa',  nombre: 'Multi-empresa',                icono: '🏢', precio_ref: 25 },
    { codigo: 'email_marketing',nombre: 'Email Marketing',              icono: '📧', precio_ref: 30 },
  ];

  const MODULOS_DISPONIBLES = [
    // Ventas & POS
    "pos", "clientes", "facturacion", "ncf", "ecf", "cotizaciones", "cxc",
    // Inventario & Logística
    "inventario", "almacenes", "transferencias", "prestamos", "importacion", "partidas_estandar",
    // Compras
    "ordenes_compra", "proveedores",
    // Contabilidad & Finanzas
    "contabilidad", "banco", "cxp", "nomina", "activos_fijos", "reportes_dgii",
    // Proyectos & Construcción
    "proyectos", "control_financiero", "contratos",
    // CRM & Comercial
    "crm", "email_marketing",
    // Renta-car
    "renta_car", "inspecciones",
    // Administración & Sistema
    "usuarios", "departamentos", "auditoria", "multi_empresa", "migracion",
  ];

  // ── INIT ───────────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/"); return; }
    const decoded = jwtDecode(token);
    if (!decoded.is_superadmin) { navigate("/dashboard"); return; }
    setUsuario(decoded);
    cargarStats();
    setLoading(false);
  }, []);

  useEffect(() => {
    if (tab === "tenants"  && tenants.length === 0)  cargarTenants();
    if (tab === "planes"   && planes.length === 0)   cargarPlanes();
    if (tab === "audit"    && audit.length === 0)    cargarAudit();
    if (tab === "paises"   && paises.length === 0)   cargarPaises();
    if (tab === "billing") { cargarBillingStats(); cargarSuscripciones(); cargarFacturas(); cargarPagos(); cargarTasas(); if (tenants.length === 0) cargarTenants(); if (planes.length === 0) cargarPlanes(); }
    if (tab === "usuarios") {
      if (tenants.length === 0) cargarTenants();
      cargarUsuarios();
    }
  }, [tab]);

  // ── CARGA DE DATOS ────────────────────────────────────────────
  const cargarStats   = async () => { try { const r = await api.get("/superadmin/stats");   setStats(r.data); } catch(e) { console.error(e); } };
  const cargarTenants = async () => { try { const r = await api.get("/superadmin/tenants"); setTenants(r.data.tenants); } catch(e) { console.error(e); } };
  const cargarPlanes  = async () => { try { const r = await api.get("/superadmin/planes");  setPlanes(r.data.planes); } catch(e) { console.error(e); } };
  const cargarAudit   = async () => { try { const r = await api.get("/superadmin/audit?limit=50"); setAudit(r.data.logs); setTotalAudit(r.data.total); } catch(e) { console.error(e); } };
  const cargarPaises  = async () => { try { const r = await api.get("/superadmin/paises"); setPaises(r.data); } catch(e) { console.error(e); } };
  const cargarBillingStats  = async () => { try { const r = await api.get("/superadmin/billing/stats"); setBillingStats(r.data.data); } catch(e) { console.error(e); } };
  const cargarSuscripciones = async (estado = filtroEstadoSus) => { try { const p = estado ? `?estado=${estado}` : ""; const r = await api.get(`/superadmin/billing/suscripciones${p}`); setSuscripciones(r.data.data); } catch(e) { console.error(e); } };
  const cargarFacturas      = async (estado = filtroEstadoFac, tenantId = filtroTenantFac) => { try { const params = new URLSearchParams(); if (estado) params.set("estado", estado); if (tenantId) params.set("tenant_id", tenantId); const r = await api.get(`/superadmin/billing/facturas?${params}`); setFacturas(r.data.data); } catch(e) { console.error(e); } };
  const cargarPagos         = async () => { try { const r = await api.get("/superadmin/billing/pagos?limit=100"); setPagos(r.data.data); } catch(e) { console.error(e); } };
  const cargarTasas         = async () => { try { const r = await api.get("/superadmin/billing/tasas?limit=200"); setTasas(r.data.data); } catch(e) { console.error(e); } };

  const abrirCrearTasa = () => {
    const hoy = new Date().toISOString().split("T")[0];
    setFormTasa({ moneda_origen:"USD", moneda_destino:"DOP", tasa:"", fecha: hoy, fuente:"manual" });
    setModalTasa("crear");
  };

  const abrirEditarTasa = (tasa) => {
    setFormTasa({ moneda_origen: tasa.moneda_origen, moneda_destino: tasa.moneda_destino, tasa: tasa.tasa, fecha: tasa.fecha, fuente: tasa.fuente });
    setModalTasa(tasa);
  };

  const accionGuardarTasa = async () => {
    if (!formTasa.moneda_origen || !formTasa.tasa || !formTasa.fecha)
      return mostrarMensaje("❌ Moneda origen, tasa y fecha son requeridos", "error");
    if (parseFloat(formTasa.tasa) <= 0)
      return mostrarMensaje("❌ La tasa debe ser mayor a 0", "error");
    setSaving(true);
    try {
      if (modalTasa === "crear") {
        await api.post("/superadmin/billing/tasas", { ...formTasa, tasa: parseFloat(formTasa.tasa) });
        mostrarMensaje("✅ Tasa registrada", "success");
      } else {
        await api.put(`/superadmin/billing/tasas/${modalTasa.id}`, { tasa: parseFloat(formTasa.tasa), fuente: formTasa.fuente });
        mostrarMensaje("✅ Tasa actualizada", "success");
      }
      setModalTasa(null);
      cargarTasas();
    } catch(e) {
      mostrarMensaje(`❌ ${e.response?.data?.error || "Error al guardar tasa"}`, "error");
    } finally { setSaving(false); }
  };

  const accionEliminarTasa = async (id) => {
    if (!window.confirm("¿Eliminar esta tasa de cambio?")) return;
    setEliminandoTasa(id);
    try {
      await api.delete(`/superadmin/billing/tasas/${id}`);
      mostrarMensaje("Tasa eliminada", "success");
      cargarTasas();
    } catch(e) {
      mostrarMensaje(`❌ ${e.response?.data?.error || "Error al eliminar"}`, "error");
    } finally { setEliminandoTasa(null); }
  };

  const cargarUsuarios = async (params = {}) => {
    try {
      const q = new URLSearchParams();
      if (params.tenant_id !== undefined) q.set("tenant_id", params.tenant_id);
      if (params.rol)    q.set("rol",    params.rol);
      if (params.buscar) q.set("buscar", params.buscar);
      const r = await api.get(`/superadmin/usuarios?${q}`);
      setUsuarios(r.data.usuarios);
    } catch(e) { console.error(e); }
  };

  const accionCrearUsuario = async () => {
    if (!formUser.nombre || !formUser.email || !formUser.password || !formUser.tenant_id)
      return mostrarMensaje("❌ Nombre, email, contraseña y empresa son requeridos", "error");
    setSaving(true);
    try {
      await api.post("/superadmin/usuarios", formUser);
      mostrarMensaje("✅ Usuario creado correctamente", "success");
      setModalUsuario(false);
      setFormUser(FORM_USER_DEFAULT);
      cargarUsuarios({ tenant_id: filtroTenant, rol: filtroRol, buscar: filtroBuscar });
    } catch(e) {
      mostrarMensaje(e.response?.data?.error || "❌ Error al crear usuario", "error");
    } finally { setSaving(false); }
  };

  const accionEditarUsuario = async () => {
    if (!modalEditarUser) return;
    setSaving(true);
    try {
      await api.patch(`/superadmin/usuarios/${modalEditarUser.id}`, formUser);
      mostrarMensaje("✅ Usuario actualizado correctamente", "success");
      setModalEditarUser(null);
      setFormUser(FORM_USER_DEFAULT);
      cargarUsuarios({ tenant_id: filtroTenant, rol: filtroRol, buscar: filtroBuscar });
    } catch(e) {
      mostrarMensaje(e.response?.data?.error || "❌ Error al actualizar usuario", "error");
    } finally { setSaving(false); }
  };

  const cargarDetalleTenant = async (id) => {
    try {
      const [r] = await Promise.all([
        api.get(`/superadmin/tenants/${id}`),
        paises.length === 0 ? cargarPaises() : Promise.resolve()
      ]);
      setTenantDetalle(r.data);
      setModalModulos(r.data);
    } catch(e) { console.error(e); }
  };

  // ── ACCIONES TENANT ───────────────────────────────────────────
  const crearTenant = async () => {
    if (!formTenant.nombre || !formTenant.plan_id) return mostrarMensaje("❌ Nombre y plan son requeridos", "error");
    if (!formTenant.nombre_admin || !formTenant.email_admin || !formTenant.password_admin) return mostrarMensaje("❌ Datos del administrador son requeridos", "error");
    setSaving(true);
    try {
      await api.post("/superadmin/tenants", formTenant);
      mostrarMensaje("✅ Empresa creada correctamente", "success");
      setModalTenant(false);
      setFormTenant({ nombre: "", rnc: "", email: "", telefono: "", plan_id: "", es_trial: false, dias_trial: 15, nombre_admin: "", email_admin: "", password_admin: "" });
      cargarTenants();
      cargarStats();
    } catch(e) {
      mostrarMensaje("❌ Error al crear empresa", "error");
    } finally { setSaving(false); }
  };

  const cambiarEstadoTenant = async (id, accion) => {
    try {
      await api.post(`/superadmin/tenants/${id}/${accion}`);
      mostrarMensaje(`✅ Empresa ${accion === 'suspender' ? 'suspendida' : 'activada'}`, "success");
      cargarTenants();
      cargarStats();
    } catch(e) { mostrarMensaje("❌ Error al cambiar estado", "error"); }
  };

  const toggleInactivoTenant = async (t) => {
    const accion = t.estado === 'inactivo' ? 'activar' : 'inactivar';
    const confirmado = window.confirm(`¿${accion === 'inactivar' ? 'Inactivar' : 'Reactivar'} la empresa "${t.nombre}"?`);
    if (!confirmado) return;
    try {
      await api.patch(`/superadmin/tenants/${t.id}/toggle-inactivo`);
      mostrarMensaje(`✅ Empresa ${accion === 'inactivar' ? 'inactivada' : 'reactivada'}`, "success");
      cargarTenants();
    } catch(e) { mostrarMensaje("❌ Error al cambiar estado", "error"); }
  };

  // ── EDITAR TENANT ─────────────────────────────────────────────
  const abrirEditarTenant = async (t) => {
    if (planes.length === 0) cargarPlanes();
    setFormEditar({ nombre: t.nombre, plan_id: String(t.plan_id || ""), estado: t.estado, metodo_pago: "" });
    setPreviewPlan(null);
    setConfirmCambio(false);
    setSuscripcionEditar(null);
    setModalEditar(t);
    // Cargar suscripción asociada para mostrar/editar método de pago
    try {
      const r = await api.get(`/superadmin/billing/suscripciones?tenant_id=${t.id}`);
      const sus = r.data.data?.[0] || null;
      setSuscripcionEditar(sus);
      if (sus) setFormEditar(prev => ({ ...prev, metodo_pago: sus.metodo_pago || "manual" }));
    } catch(e) { /* sin suscripción — silencioso */ }
    // Cargar add-ons
    setLoadingAddons(true);
    setAddonsTenant({ asignados: [], disponibles: [] });
    try {
      const ra = await api.get(`/superadmin/tenants/${t.id}/addons`);
      setAddonsTenant(ra.data);
      const precios = {};
      (ra.data.asignados || []).forEach(a => { precios[a.codigo] = String(a.precio_acordado ?? 0); });
      setPreciosAddon(precios);
    } catch(e) { /* silencioso */ }
    finally { setLoadingAddons(false); }
  };

  const cerrarEditarTenant = () => {
    setModalEditar(null);
    setPreviewPlan(null);
    setConfirmCambio(false);
    setSuscripcionEditar(null);
    setAddonsTenant({ asignados: [], disponibles: [] });
    setPreciosAddon({});
    setSavingAddon(null);
  };

  // ── ADD-ONS TENANT ────────────────────────────────────────────
  const recargarAddonsTenant = async (tenantId) => {
    try {
      const ra = await api.get(`/superadmin/tenants/${tenantId}/addons`);
      setAddonsTenant(ra.data);
      const precios = {};
      (ra.data.asignados || []).forEach(a => { precios[a.codigo] = String(a.precio_acordado ?? 0); });
      setPreciosAddon(precios);
    } catch(e) { /* silencioso */ }
  };

  const toggleAddonTenant = async (codigo, nombre, activar) => {
    const tenantId = modalEditar.id;
    setSavingAddon(codigo);
    try {
      if (activar) {
        const asignado = addonsTenant.asignados.find(a => a.codigo === codigo);
        await api.post(`/superadmin/tenants/${tenantId}/addons/activar`, {
          addon_codigo: codigo,
          precio_acordado: parseFloat(preciosAddon[codigo] ?? asignado?.precio_acordado ?? ADDONS_PAGO.find(a => a.codigo === codigo)?.precio_ref ?? 0),
          facturar: asignado?.facturar ?? false,
          ciclo_facturacion: 'mensual',
        });
      } else {
        await api.post(`/superadmin/tenants/${tenantId}/addons/desactivar`, { addon_codigo: codigo });
      }
      await recargarAddonsTenant(tenantId);
      mostrarMensaje(`✅ Add-on "${nombre}" ${activar ? 'activado' : 'desactivado'}`, "success");
    } catch(e) {
      mostrarMensaje(`❌ ${e.response?.data?.error || "Error al cambiar add-on"}`, "error");
    } finally { setSavingAddon(null); }
  };

  const toggleFacturarAddon = async (codigo, nombre, facturar) => {
    const tenantId = modalEditar.id;
    const asignado = addonsTenant.asignados.find(a => a.codigo === codigo && a.estado === 'activo');
    if (!asignado) return;
    setSavingAddon(codigo);
    try {
      await api.post(`/superadmin/tenants/${tenantId}/addons/activar`, {
        addon_codigo: codigo,
        precio_acordado: parseFloat(preciosAddon[codigo] ?? asignado.precio_acordado ?? 0),
        facturar,
        ...(facturar && { ciclo_facturacion: asignado.ciclo_facturacion || 'mensual' }),
      });
      await recargarAddonsTenant(tenantId);
      mostrarMensaje(`✅ Facturación de "${nombre}" ${facturar ? 'activada' : 'desactivada'}`, "success");
    } catch(e) {
      mostrarMensaje(`❌ ${e.response?.data?.error || "Error al cambiar facturación"}`, "error");
    } finally { setSavingAddon(null); }
  };

  const guardarPrecioAddon = async (codigo, nombre) => {
    const tenantId = modalEditar.id;
    const asignado = addonsTenant.asignados.find(a => a.codigo === codigo && a.estado === 'activo');
    if (!asignado) return;
    setSavingAddon(codigo);
    try {
      await api.post(`/superadmin/tenants/${tenantId}/addons/activar`, {
        addon_codigo: codigo,
        precio_acordado: parseFloat(preciosAddon[codigo] ?? 0),
        facturar: asignado.facturar,
        ...(asignado.facturar && { ciclo_facturacion: asignado.ciclo_facturacion || 'mensual' }),
      });
      await recargarAddonsTenant(tenantId);
      mostrarMensaje(`✅ Precio de "${nombre}" actualizado`, "success");
    } catch(e) {
      mostrarMensaje(`❌ ${e.response?.data?.error || "Error al guardar precio"}`, "error");
    } finally { setSavingAddon(null); }
  };

  const onChangePlanEditar = async (nuevoPlanId) => {
    setFormEditar(prev => ({ ...prev, plan_id: nuevoPlanId }));
    if (!nuevoPlanId || nuevoPlanId === String(modalEditar.plan_id)) {
      setPreviewPlan(null);
      return;
    }
    setLoadingPreview(true);
    try {
      const r = await api.get(`/superadmin/tenants/${modalEditar.id}/preview-cambio-plan?nuevo_plan_id=${nuevoPlanId}`);
      setPreviewPlan(r.data);
    } catch(e) {
      mostrarMensaje("❌ Error al cargar preview del plan", "error");
      setPreviewPlan(null);
    } finally { setLoadingPreview(false); }
  };

  const guardarEdicionTenant = async () => {
    if (!formEditar.nombre.trim()) return mostrarMensaje("❌ El nombre es requerido", "error");

    const planCambio = formEditar.plan_id && formEditar.plan_id !== String(modalEditar.plan_id);
    const estadoCambio = formEditar.estado !== modalEditar.estado;

    // Si cambia el plan, exigir confirmación previa
    if (planCambio && !confirmCambio) {
      setConfirmCambio(true);
      return;
    }

    setSaving(true);
    try {
      // 1. Actualizar nombre (siempre)
      await api.put(`/superadmin/tenants/${modalEditar.id}`, { nombre: formEditar.nombre.trim() });

      // 2. Cambiar estado si corresponde
      if (estadoCambio) {
        if (formEditar.estado === 'suspendido') {
          await api.post(`/superadmin/tenants/${modalEditar.id}/suspender`);
        } else if (formEditar.estado === 'activo') {
          await api.post(`/superadmin/tenants/${modalEditar.id}/activar`);
        }
      }

      // 3. Cambiar plan si corresponde (activa/desactiva módulos automáticamente)
      if (planCambio) {
        await api.post(`/superadmin/tenants/${modalEditar.id}/cambiar-plan`, { plan_id: formEditar.plan_id });
      }

      // 4. Cambiar método de pago de la suscripción si corresponde
      if (suscripcionEditar && formEditar.metodo_pago &&
          formEditar.metodo_pago !== (suscripcionEditar.metodo_pago || "manual")) {
        await api.put(`/superadmin/billing/suscripciones/${suscripcionEditar.id}`, {
          metodo_pago: formEditar.metodo_pago,
        });
      }

      mostrarMensaje("✅ Empresa actualizada correctamente", "success");
      cerrarEditarTenant();
      cargarTenants();
      cargarStats();
    } catch(e) {
      mostrarMensaje("❌ Error al actualizar empresa", "error");
    } finally { setSaving(false); }
  };

  // ── TRIAL ─────────────────────────────────────────────────────
  const guardarExtenderTrial = async () => {
    if (!diasExtra || diasExtra <= 0) return mostrarMensaje("❌ Ingresa un número válido de días", "error");
    setSaving(true);
    try {
      await api.patch(`/superadmin/tenants/${modalExtenderTrial.id}/extender-trial`, { dias_extra: diasExtra });
      mostrarMensaje(`✅ Trial extendido ${diasExtra} días`, "success");
      setModalExtenderTrial(null);
      cargarTenants();
    } catch(e) { mostrarMensaje("❌ Error al extender trial", "error"); }
    finally { setSaving(false); }
  };

  const guardarConvertirPlan = async () => {
    if (!planConvertir) return mostrarMensaje("❌ Selecciona un plan", "error");
    setSaving(true);
    try {
      await api.post(`/superadmin/tenants/${modalConvertir.id}/convertir-plan`, { plan_id: planConvertir });
      mostrarMensaje("✅ Trial convertido a plan pagado", "success");
      setModalConvertir(null);
      cargarTenants();
      cargarStats();
    } catch(e) { mostrarMensaje("❌ Error al convertir trial", "error"); }
    finally { setSaving(false); }
  };

  const toggleModulo = async (tenantId, modulo, activo) => {
    try {
      await api.post(`/superadmin/tenants/${tenantId}/modulos`, { modulo, activo });
      // Actualizar estado local: si el módulo ya existe, actualiza; si no, lo inserta
      setModalModulos(prev => {
        const existe = prev.modulos.some(m => m.modulo === modulo);
        const modulosActualizados = existe
          ? prev.modulos.map(m => m.modulo === modulo ? { ...m, activo } : m)
          : [...prev.modulos, { modulo, activo, fecha_activacion: new Date().toISOString(), notas: null }];
        return { ...prev, modulos: modulosActualizados };
      });
      mostrarMensaje(`✅ Módulo "${modulo}" ${activo ? 'activado' : 'desactivado'}`, "success");
    } catch(e) { mostrarMensaje("❌ Error al cambiar módulo", "error"); }
  };

  // ── ACCIONES PLAN ─────────────────────────────────────────────
  const crearPlan = async () => {
    if (!formPlan.nombre || !formPlan.precio_mensual) return mostrarMensaje("❌ Nombre y precio son requeridos", "error");
    setSaving(true);
    try {
      if (planEditando) {
        await api.put(`/superadmin/planes/${planEditando.id}`, formPlan);
        mostrarMensaje("✅ Plan actualizado correctamente", "success");
      } else {
        await api.post("/superadmin/planes", formPlan);
        mostrarMensaje("✅ Plan creado correctamente", "success");
      }
      setModalPlan(false);
      setPlanEditando(null);
      setFormPlan({ nombre: "", descripcion: "", precio_mensual: "", modulos_incluidos: [] });
      cargarPlanes();
    } catch(e) {
      mostrarMensaje(`❌ Error al ${planEditando ? 'actualizar' : 'crear'} plan`, "error");
    } finally { setSaving(false); }
  };

  const abrirEditarPlan = (plan) => {
    setPlanEditando(plan);
    setFormPlan({
      nombre: plan.nombre,
      descripcion: plan.descripcion || "",
      precio_mensual: plan.precio_mensual,
      modulos_incluidos: plan.modulos_incluidos || [],
    });
    setModalPlan(true);
  };

  const toggleModuloPlan = (modulo) => {
    setFormPlan(prev => ({
      ...prev,
      modulos_incluidos: prev.modulos_incluidos.includes(modulo)
        ? prev.modulos_incluidos.filter(m => m !== modulo)
        : [...prev.modulos_incluidos, modulo]
    }));
  };

  // ── ACCIONES PAÍSES ───────────────────────────────────────────
  const abrirModalPais = (pais = null) => {
    if (pais) {
      setPaisEditando(pais);
      setFormPais({
        codigo: pais.codigo, nombre: pais.nombre, nombre_fiscal: pais.nombre_fiscal || "",
        moneda_codigo: pais.moneda_codigo, moneda_nombre: pais.moneda_nombre, moneda_simbolo: pais.moneda_simbolo, moneda_decimales: pais.moneda_decimales,
        zona_horaria: pais.zona_horaria, formato_fecha: pais.formato_fecha || "DD/MM/YYYY",
        separador_decimal: pais.separador_decimal || ".", separador_miles: pais.separador_miles || ",",
        id_fiscal_empresa_label: pais.id_fiscal_empresa_label, id_fiscal_empresa_regex: pais.id_fiscal_empresa_regex || "", id_fiscal_empresa_mask: pais.id_fiscal_empresa_mask || "",
        id_fiscal_persona_label: pais.id_fiscal_persona_label || "", id_fiscal_persona_regex: pais.id_fiscal_persona_regex || "", id_fiscal_persona_mask: pais.id_fiscal_persona_mask || "",
        impuesto_label: pais.impuesto_label, impuesto_tasa_default: pais.impuesto_tasa_default,
        sistema_comprobantes: pais.sistema_comprobantes || "", comprobantes_activo: pais.comprobantes_activo,
        ente_regulador_nombre: pais.ente_regulador_nombre || "", ente_regulador_url: pais.ente_regulador_url || "",
        activo: pais.activo, orden: pais.orden
      });
    } else {
      setPaisEditando(null);
      setFormPais({
        codigo: "", nombre: "", nombre_fiscal: "",
        moneda_codigo: "USD", moneda_nombre: "", moneda_simbolo: "$", moneda_decimales: 2,
        zona_horaria: "America/Santo_Domingo", formato_fecha: "DD/MM/YYYY",
        separador_decimal: ".", separador_miles: ",",
        id_fiscal_empresa_label: "RUC", id_fiscal_empresa_regex: "", id_fiscal_empresa_mask: "",
        id_fiscal_persona_label: "Cédula", id_fiscal_persona_regex: "", id_fiscal_persona_mask: "",
        impuesto_label: "IVA", impuesto_tasa_default: "",
        sistema_comprobantes: "", comprobantes_activo: true,
        ente_regulador_nombre: "", ente_regulador_url: "",
        activo: true, orden: 99
      });
    }
    setModalPais(true);
  };

  const guardarPais = async () => {
    if (!formPais.codigo || !formPais.nombre || !formPais.impuesto_label || !formPais.impuesto_tasa_default) {
      return mostrarMensaje("❌ Código, nombre, impuesto y tasa son requeridos", "error");
    }
    setSaving(true);
    try {
      if (paisEditando) {
        await api.put(`/superadmin/paises/${paisEditando.codigo}`, formPais);
        mostrarMensaje("✅ País actualizado correctamente", "success");
      } else {
        await api.post("/superadmin/paises", formPais);
        mostrarMensaje("✅ País creado correctamente", "success");
      }
      setModalPais(false);
      setPaises([]);
      cargarPaises();
    } catch(e) {
      mostrarMensaje(e.response?.data?.error || "❌ Error al guardar país", "error");
    } finally { setSaving(false); }
  };

  const toggleActivoPais = async (codigo, activo) => {
    try {
      await api.patch(`/superadmin/paises/${codigo}/toggle`, { activo });
      setPaises(prev => prev.map(p => p.codigo === codigo ? { ...p, activo } : p));
      mostrarMensaje(`✅ País ${activo ? "activado" : "desactivado"}`, "success");
    } catch(e) { mostrarMensaje("❌ Error al cambiar estado", "error"); }
  };

  // ── ACCIONES BILLING ──────────────────────────────────────────
  const accionGenerarFactura = async (tenant_id, tenant_nombre) => {
    if (!window.confirm(`¿Generar factura del período actual para "${tenant_nombre}"?`)) return;
    setGenerandoFactura(tenant_id);
    try {
      const r = await api.post("/superadmin/billing/generar-factura", { tenant_id });
      mostrarMensaje(`✅ Factura ${r.data.periodo} generada — ${r.data.monto}`, "success");
      cargarFacturas(filtroEstadoFac, filtroTenantFac);
      cargarBillingStats();
      cargarSuscripciones();
    } catch(e) {
      mostrarMensaje(`❌ ${e.response?.data?.error || "Error al generar factura"}`, "error");
    } finally { setGenerandoFactura(null); }
  };

  const accionCrearSuscripcion = async () => {
    if (!formSus.tenant_id || !formSus.plan_id || !formSus.precio_acordado)
      return mostrarMensaje("❌ Empresa, plan y precio son requeridos", "error");
    setSaving(true);
    try {
      await api.post("/superadmin/billing/suscripciones", { ...formSus, precio_acordado: parseFloat(formSus.precio_acordado), descuento_pct: parseFloat(formSus.descuento_pct || 0) });
      mostrarMensaje("✅ Suscripción creada", "success");
      setModalCrearSus(false);
      setFormSus({ tenant_id:"", plan_id:"", tipo_ciclo:"mensual", precio_acordado:"", descuento_pct:"0", moneda:"DOP", metodo_pago:"manual", notas:"" });
      cargarSuscripciones();
      cargarTenants();
    } catch(e) {
      mostrarMensaje(`❌ ${e.response?.data?.error || "Error al crear suscripción"}`, "error");
    } finally { setSaving(false); }
  };

  const abrirEditarSus = (sus) => {
    setFormEditarSus({
      metodo_pago:     sus.metodo_pago     || "manual",
      precio_acordado: String(sus.precio_acordado),
      descuento_pct:   String(sus.descuento_pct || 0),
      moneda:          sus.moneda          || "DOP",
      tipo_ciclo:      sus.tipo_ciclo      || "mensual",
      notas:           sus.notas           || "",
    });
    setModalEditarSus(sus);
  };

  const guardarEdicionSus = async () => {
    if (!formEditarSus.precio_acordado) return mostrarMensaje("❌ El precio es requerido", "error");
    setSaving(true);
    try {
      await api.put(`/superadmin/billing/suscripciones/${modalEditarSus.id}`, {
        ...formEditarSus,
        precio_acordado: parseFloat(formEditarSus.precio_acordado),
        descuento_pct:   parseFloat(formEditarSus.descuento_pct || 0),
      });
      mostrarMensaje("✅ Suscripción actualizada", "success");
      setModalEditarSus(null);
      cargarSuscripciones();
      cargarBillingStats();
    } catch(e) {
      mostrarMensaje(`❌ ${e.response?.data?.error || "Error al actualizar suscripción"}`, "error");
    } finally { setSaving(false); }
  };

  const accionRegistrarPago = async () => {
    if (!modalPagar || !formPago.monto) return mostrarMensaje("❌ Monto es requerido", "error");
    setSaving(true);
    try {
      await api.post(`/superadmin/billing/facturas/${modalPagar.id}/pagar`, { ...formPago, monto: parseFloat(formPago.monto) });
      mostrarMensaje("✅ Pago registrado. Tenant reactivado si estaba suspendido.", "success");
      setModalPagar(null);
      setFormPago({ monto:"", moneda:"DOP", metodo:"manual", referencia_pago:"", cuenta_bancaria_id:"", notas:"" });
      cargarFacturas();
      cargarSuscripciones();
      cargarBillingStats();
      cargarPagos();
    } catch(e) {
      mostrarMensaje(`❌ ${e.response?.data?.error || "Error al registrar pago"}`, "error");
    } finally { setSaving(false); }
  };

  // ── HELPERS ───────────────────────────────────────────────────
  const mostrarMensaje = (texto, tipo) => {
    setMensaje({ texto, tipo });
    setTimeout(() => setMensaje(null), 3500);
  };

  const estadoColor = (estado) => ({
    activo:     { bg: "#064e3b", color: "#6ee7b7" },
    suspendido: { bg: "#7f1d1d", color: "#fca5a5" },
    pendiente:  { bg: "#78350f", color: "#fcd34d" },
    cancelado:  { bg: "#1f2937", color: "#9ca3af" },
    inactivo:   { bg: "#1e293b", color: "#64748b" },
  }[estado] || { bg: "#1f2937", color: "#9ca3af" });

  const formatFecha = (f) => f ? new Date(String(f).substring(0,10) + 'T00:00:00').toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  if (loading) return (
    <div style={s.loadingWrap}>
      <div style={s.spinner}></div>
      <p style={{ color: "#94a3b8" }}>Cargando panel...</p>
    </div>
  );

  // ════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════
  return (
    <div style={s.root}>

      {/* ── HEADER ─────────────────────────────────────────────── */}
      <div style={s.header}>
        <div style={s.headerLeft}>
          <span style={s.crown}>👑</span>
          <div>
            <h1 style={s.headerTitle}>Super Admin Panel</h1>
            <p style={s.headerSub}>eFIT Multiempresa · {usuario?.email}</p>
          </div>
        </div>
        <button onClick={() => navigate("/dashboard")} style={s.btnVolver}>
          ← Volver al Dashboard
        </button>
      </div>

      {/* ── MENSAJE GLOBAL ─────────────────────────────────────── */}
      {mensaje && (
        <div style={{ ...s.mensajeGlobal, background: mensaje.tipo === "success" ? "#064e3b" : "#7f1d1d", borderColor: mensaje.tipo === "success" ? "#6ee7b7" : "#fca5a5", color: mensaje.tipo === "success" ? "#6ee7b7" : "#fca5a5" }}>
          {mensaje.texto}
        </div>
      )}

      {/* ── TABS ───────────────────────────────────────────────── */}
      <div style={s.tabBar}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ ...s.tabBtn, ...(tab === t.id ? s.tabBtnActive : {}) }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={s.content}>

        {/* ════════════════════════════════════════════════════════
            TAB: OVERVIEW / STATS
        ════════════════════════════════════════════════════════ */}
        {tab === "stats" && stats && (
          <div>
            <div style={s.statsGrid}>
              <StatCard label="Empresas Activas"    valor={stats.tenants?.tenants_activos}    color="#6ee7b7" icon="✅" />
              <StatCard label="Empresas Trial"      valor={stats.tenants?.tenants_trial}      color="#fcd34d" icon="⏳" />
              <StatCard label="Suspendidas"         valor={stats.tenants?.tenants_suspendidos} color="#fca5a5" icon="⛔" />
              <StatCard label="Total Usuarios"      valor={stats.total_usuarios}              color="#a5b4fc" icon="👥" />
            </div>

            <h3 style={s.sectionTitle}>Distribución por Plan</h3>
            <div style={s.planesGrid}>
              {stats.por_plan?.map(p => (
                <div key={p.nombre} style={s.planStatCard}>
                  <p style={s.planStatNombre}>{p.nombre}</p>
                  <p style={s.planStatTotal}>{p.total}</p>
                  <p style={s.planStatLabel}>empresas</p>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
              <button onClick={() => setTab("tenants")} style={s.btnPrimary}>🏢 Ver Empresas</button>
              <button onClick={() => setTab("planes")}  style={s.btnSecondary}>📦 Ver Planes</button>
              <button onClick={() => setTab("audit")}   style={s.btnSecondary}>🛡️ Ver Audit Log</button>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════
            TAB: EMPRESAS / TENANTS
        ════════════════════════════════════════════════════════ */}
        {tab === "tenants" && (
          <div>
            {/* Alerta: trials próximos a vencer (≤3 días) */}
            {tenants.filter(t => t.es_trial && t.dias_trial_restantes <= 3 && t.dias_trial_restantes >= 0).length > 0 && (
              <div style={{ background:'#7f1d1d', border:'1px solid #ef4444', borderRadius:10, padding:'12px 18px', marginBottom:16, display:'flex', alignItems:'center', gap:12 }}>
                <span style={{fontSize:20}}>🔴</span>
                <p style={{margin:0, color:'#fca5a5', fontWeight:600, fontSize:14}}>
                  {tenants.filter(t => t.es_trial && t.dias_trial_restantes <= 3 && t.dias_trial_restantes >= 0).length} empresa(s) con trial venciendo en 3 días o menos:
                  {' '}{tenants.filter(t => t.es_trial && t.dias_trial_restantes <= 3 && t.dias_trial_restantes >= 0).map(t => t.nombre).join(', ')}
                </p>
              </div>
            )}
            <div style={s.tabHeader}>
              <h2 style={s.tabTitle}>Empresas registradas</h2>
              <button onClick={() => { if (planes.length === 0) cargarPlanes(); setModalTenant(true); }} style={s.btnPrimary}>+ Nueva Empresa</button>
            </div>

            <div style={s.tabla}>
              {/* Cabecera */}
              <div style={s.tablaHead}>
                <span style={{flex:0.5, color:"#6366f1"}}>ID</span>
                <span style={{flex:2}}>Empresa</span>
                <span style={{flex:1}}>Plan</span>
                <span style={{flex:1}}>Estado</span>
                <span style={{flex:1}}>Usuarios</span>
                <span style={{flex:1}}>Módulos</span>
                <span style={{flex:1}}>Registro</span>
                <span style={{flex:1.5}}>Acciones</span>
              </div>

              {tenants.length === 0 && (
                <div style={s.tablaEmpty}>No hay empresas registradas</div>
              )}

              {tenants.map(t => {
                const ec = estadoColor(t.estado);
                return (
                  <div key={t.id} style={s.tablaRow}>
                    <div style={{flex:0.5, display:"flex", alignItems:"center"}}>
                      <span style={{background:"#312e81", color:"#a5b4fc", padding:"2px 8px", borderRadius:6, fontSize:12, fontWeight:700}}>#{t.id}</span>
                    </div>
                    <div style={{flex:2}}>
                      <p style={s.tenantNombre}>{t.nombre}</p>
                      <p style={s.tenantRnc}>{t.rnc || "Sin RNC"} · {t.email || "—"}</p>
                    </div>
                    <div style={{flex:1}}>
                      <span style={{color:"#94a3b8"}}>{t.plan_nombre || "—"}</span>
                      {t.es_trial && (() => {
                        const d = t.dias_trial_restantes;
                        const hoy = new Date(); hoy.setHours(0,0,0,0);
                        const exp = t.fecha_expiracion ? new Date(t.fecha_expiracion) : null;
                        const vencido = exp && exp < hoy;
                        const bg = vencido ? '#7f1d1d' : d <= 3 ? '#7f1d1d' : d <= 7 ? '#78350f' : '#064e3b';
                        const cl = vencido ? '#fca5a5' : d <= 3 ? '#fca5a5' : d <= 7 ? '#fcd34d' : '#6ee7b7';
                        return <span style={{...s.badge, background:bg, color:cl, marginLeft:6, fontSize:11}}>
                          {vencido ? '⛔ Vencido' : `⏳ ${d}d`}
                        </span>;
                      })()}
                    </div>
                    <div style={{flex:1}}>
                      <span style={{...s.badge, background: ec.bg, color: ec.color}}>{t.estado}</span>
                    </div>
                    <span style={{flex:1, color:"#94a3b8"}}>{t.total_usuarios}</span>
                    <span style={{flex:1, color:"#6ee7b7"}}>{t.modulos_activos} activos</span>
                    <span style={{flex:1, color:"#64748b", fontSize:12}}>{formatFecha(t.fecha_registro)}</span>
                    <div style={{flex:1.5, display:'flex', gap:6, flexWrap:'wrap'}}>
                      <button onClick={() => abrirEditarTenant(t)} style={{...s.btnMini, color:'#93c5fd', borderColor:'#3b82f6'}}>✏️ Editar</button>
                      <button onClick={() => cargarDetalleTenant(t.id)} style={s.btnMini}>⚙️ Módulos</button>
                      {(t.estado === 'activo' || t.estado === 'inactivo') && (
                        <button
                          onClick={() => toggleInactivoTenant(t)}
                          style={{...s.btnMini, color: t.estado === 'inactivo' ? '#6ee7b7' : '#94a3b8', borderColor: t.estado === 'inactivo' ? '#059669' : '#475569'}}
                        >
                          {t.estado === 'inactivo' ? '✅ Reactivar' : '⏸ Inactivar'}
                        </button>
                      )}
                      {t.es_trial && (
                        <>
                          <button onClick={() => { setDiasExtra(15); setModalExtenderTrial(t); }} style={{...s.btnMini, color:'#fcd34d', borderColor:'#f59e0b'}}>⏳ Extender</button>
                          <button onClick={() => { setPlanConvertir(""); setModalConvertir(t); }} style={{...s.btnMini, color:'#6ee7b7', borderColor:'#059669'}}>💳 Convertir</button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════
            TAB: USUARIOS GLOBAL
        ════════════════════════════════════════════════════════ */}
        {tab === "usuarios" && (
          <div>
            <div style={s.tabHeader}>
              <div>
                <h2 style={s.tabTitle}>Usuarios globales</h2>
                <p style={{ color: "#94a3b8", fontSize: 13, margin: 0 }}>
                  Todos los usuarios de todas las empresas
                  {usuarios.filter(u => !u.tenant_id).length > 0 && (
                    <span style={{ marginLeft: 10, ...s.badge, background: "#7f1d1d", color: "#fca5a5" }}>
                      ⚠️ {usuarios.filter(u => !u.tenant_id).length} huérfano{usuarios.filter(u => !u.tenant_id).length !== 1 ? 's' : ''}
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={() => { setFormUser(FORM_USER_DEFAULT); setModalUsuario(true); }}
                style={s.btnPrimary}
              >
                + Nuevo Usuario
              </button>
            </div>

            {/* Filtros */}
            <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
              <input
                style={{ ...s.input, flex: 2, minWidth: 200 }}
                placeholder="🔍 Buscar por nombre o email..."
                value={filtroBuscar}
                onChange={e => setFiltroBuscar(e.target.value)}
                onKeyDown={e => e.key === "Enter" && cargarUsuarios({ tenant_id: filtroTenant, rol: filtroRol, buscar: filtroBuscar })}
              />
              <select
                style={{ ...s.input, flex: 1, minWidth: 180 }}
                value={filtroTenant}
                onChange={e => { setFiltroTenant(e.target.value); cargarUsuarios({ tenant_id: e.target.value, rol: filtroRol, buscar: filtroBuscar }); }}
              >
                <option value="">Todas las empresas</option>
                {tenants.map(t => <option key={t.id} value={t.id}>#{t.id} {t.nombre}</option>)}
              </select>
              <select
                style={{ ...s.input, flex: 1, minWidth: 140 }}
                value={filtroRol}
                onChange={e => { setFiltroRol(e.target.value); cargarUsuarios({ tenant_id: filtroTenant, rol: e.target.value, buscar: filtroBuscar }); }}
              >
                <option value="">Todos los roles</option>
                <optgroup label="— Generales —">
                  {ROLES_USUARIO.map(r => <option key={r} value={r}>{r}</option>)}
                </optgroup>
                <optgroup label="— Contables —">
                  {ROLES_CONTABLES.map(r => <option key={r} value={r}>{r}</option>)}
                </optgroup>
              </select>
              <button
                style={s.btnSecondary}
                onClick={() => cargarUsuarios({ tenant_id: filtroTenant, rol: filtroRol, buscar: filtroBuscar })}
              >
                🔄 Buscar
              </button>
            </div>

            {/* Tabla */}
            <div style={s.tabla}>
              <div style={s.tablaHead}>
                <span style={{ flex: 0.4 }}>ID</span>
                <span style={{ flex: 2 }}>Nombre / Email</span>
                <span style={{ flex: 1 }}>Rol</span>
                <span style={{ flex: 2 }}>Empresa</span>
                <span style={{ flex: 0.8 }}>Estado</span>
                <span style={{ flex: 1.2 }}>Acciones</span>
              </div>

              {usuarios.length === 0 && (
                <div style={s.tablaEmpty}>No hay usuarios que coincidan</div>
              )}

              {usuarios.map(u => (
                <div key={u.id} style={s.tablaRow}>
                  <div style={{ flex: 0.4 }}>
                    <span style={{ background: "#312e81", color: "#a5b4fc", padding: "2px 7px", borderRadius: 6, fontSize: 12, fontWeight: 700 }}>#{u.id}</span>
                  </div>
                  <div style={{ flex: 2 }}>
                    <p style={{ margin: 0, fontWeight: 600, color: "#f1f5f9", fontSize: 14 }}>{u.nombre}</p>
                    <p style={{ margin: "2px 0 0", fontSize: 12, color: "#64748b" }}>{u.email}</p>
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ ...s.badge, background: "#1e3a5f", color: "#93c5fd" }}>{u.rol}</span>
                  </div>
                  <div style={{ flex: 2 }}>
                    {u.tenant_id ? (
                      <span style={{ color: "#94a3b8", fontSize: 13 }}>
                        <span style={{ color: "#64748b" }}>#{u.tenant_id}</span> {u.tenant_nombre}
                      </span>
                    ) : (
                      <span style={{ ...s.badge, background: "#7f1d1d", color: "#fca5a5" }}>⚠️ Sin empresa (huérfano)</span>
                    )}
                  </div>
                  <div style={{ flex: 0.8 }}>
                    <span style={{ ...s.badge, ...(u.activo ? { background: "#064e3b", color: "#6ee7b7" } : { background: "#374151", color: "#9ca3af" }) }}>
                      {u.activo ? "Activo" : "Inactivo"}
                    </span>
                  </div>
                  <div style={{ flex: 1.2, display: "flex", gap: 6 }}>
                    <button
                      onClick={() => {
                        setFormUser({
                          nombre: u.nombre,
                          email: u.email,
                          password: "",
                          rol: u.rol,
                          tenant_id: u.tenant_id ? String(u.tenant_id) : "",
                          activo: u.activo,
                        });
                        setModalEditarUser(u);
                      }}
                      style={{ ...s.btnMini, color: "#93c5fd", borderColor: "#3b82f6" }}
                    >
                      ✏️ Editar
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <p style={{ color: "#475569", fontSize: 12, marginTop: 10 }}>
              {usuarios.length} usuario{usuarios.length !== 1 ? 's' : ''} mostrado{usuarios.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════
            TAB: PLANES
        ════════════════════════════════════════════════════════ */}
        {tab === "planes" && (
          <div>
            <div style={s.tabHeader}>
              <h2 style={s.tabTitle}>Planes disponibles</h2>
              <button onClick={() => setModalPlan(true)} style={s.btnPrimary}>+ Nuevo Plan</button>
            </div>

            <div style={s.planesCards}>
              {planes.map(p => (
                <div key={p.id} style={s.planCard}>
                  <div style={s.planCardHeader}>
                    <h3 style={s.planCardNombre}>{p.nombre}</h3>
                    <span style={s.planCardPrecio}>${p.precio_mensual}<span style={{fontSize:12, opacity:0.7}}>/mes</span></span>
                  </div>
                  {p.descripcion && <p style={s.planCardDesc}>{p.descripcion}</p>}
                  <div style={s.planCardModulos}>
                    {(p.modulos_incluidos || []).map(m => (
                      <span key={m} style={s.moduloBadge}>{m}</span>
                    ))}
                  </div>
                  <div style={s.planCardFooter}>
                    <span style={{color:"#94a3b8", fontSize:13}}>{p.total_tenants} empresa{p.total_tenants !== 1 ? 's' : ''}</span>
                    <div style={{display:"flex", gap:8, alignItems:"center"}}>
                      <span style={{...s.badge, background: p.activo ? "#064e3b" : "#374151", color: p.activo ? "#6ee7b7" : "#9ca3af"}}>
                        {p.activo ? "Activo" : "Inactivo"}
                      </span>
                      <button onClick={() => abrirEditarPlan(p)} style={{...s.btnMini, color:'#a5b4fc', borderColor:'#4f46e5'}}>✏️ Editar</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════════════════════════
            TAB: BILLING — SUSCRIPCIONES Y COBROS
        ════════════════════════════════════════════════════════ */}
        {tab === "billing" && (
          <div>
            <div style={s.tabHeader}>
              <h2 style={s.tabTitle}>💳 Billing — Suscripciones y Cobros</h2>
              <button onClick={() => setModalCrearSus(true)} style={s.btnPrimary}>+ Nueva suscripción</button>
            </div>

            {/* ── KPI Cards ── */}
            {billingStats && (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(160px,1fr))", gap:12, marginBottom:24 }}>
                {[
                  { label:"Suscripciones activas", valor: billingStats.suscripciones_activas, color:"#6ee7b7", icon:"✅" },
                  { label:"MRR",                   valor: `DOP ${parseFloat(billingStats.mrr).toLocaleString("es-DO",{minimumFractionDigits:2})}`, color:"#a5b4fc", icon:"💰" },
                  { label:"Ingresos cobrados",      valor: `DOP ${parseFloat(billingStats.ingresos_cobrados).toLocaleString("es-DO",{minimumFractionDigits:2})}`, color:"#6ee7b7", icon:"🏦" },
                  { label:"Facturas pendientes",    valor: billingStats.facturas_pendientes, color: billingStats.facturas_pendientes > 0 ? "#fb923c" : "#6ee7b7", icon:"🧾" },
                  { label:"Monto en riesgo",        valor: `DOP ${parseFloat(billingStats.monto_pendiente).toLocaleString("es-DO",{minimumFractionDigits:2})}`, color: billingStats.monto_pendiente > 0 ? "#f87171" : "#6ee7b7", icon:"⚠️" },
                  { label:"Tenants en riesgo",      valor: billingStats.tenants_en_riesgo, color: billingStats.tenants_en_riesgo > 0 ? "#f87171" : "#6ee7b7", icon:"🔴" },
                  { label:"Tasa de morosidad",      valor: `${billingStats.tasa_morosidad}%`, color: parseFloat(billingStats.tasa_morosidad) > 10 ? "#f87171" : "#6ee7b7", icon:"📊" },
                ].map(k => (
                  <div key={k.label} style={{ background:"#1e293b", border:"1px solid #334155", borderRadius:10, padding:"14px 16px", textAlign:"center" }}>
                    <span style={{ fontSize:24 }}>{k.icon}</span>
                    <p style={{ margin:"6px 0 2px", fontSize:20, fontWeight:800, color:k.color }}>{k.valor}</p>
                    <p style={{ margin:0, fontSize:11, color:"#64748b" }}>{k.label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Sub-tabs */}
            <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap:"wrap" }}>
              {[{id:"suscripciones",label:"📋 Suscripciones"},{id:"facturas",label:"🧾 Facturas"},{id:"pagos",label:"💸 Pagos"},{id:"tasas",label:"💱 Tasas de Cambio"}].map(st => (
                <button key={st.id} onClick={() => { setBillingSubTab(st.id); if (st.id !== "facturas") setFiltroTenantFac(""); }}
                  style={{ ...s.tabBtn, ...(billingSubTab === st.id ? s.tabBtnActive : {}) }}>
                  {st.label}
                </button>
              ))}
            </div>

            {/* ── SUB-TAB: SUSCRIPCIONES ── */}
            {billingSubTab === "suscripciones" && (
              <>
                <div style={{ display:"flex", gap:8, marginBottom:16, alignItems:"center" }}>
                  <select style={{ ...s.input, width:180 }} value={filtroEstadoSus}
                    onChange={e => { setFiltroEstadoSus(e.target.value); cargarSuscripciones(e.target.value); }}>
                    <option value="">Todos los estados</option>
                    <option value="activa">Activa</option>
                    <option value="gracia">Gracia</option>
                    <option value="suspendida">Suspendida</option>
                    <option value="cancelada">Cancelada</option>
                  </select>
                  <button onClick={() => cargarSuscripciones()} style={s.btnMini}>↺ Actualizar</button>
                </div>
                <div style={s.tabla}>
                  <div style={{ ...s.tablaHead, gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 1fr 180px", display:"grid" }}>
                    <span>Empresa</span><span>Plan</span><span>Ciclo</span><span>Precio</span><span>Próximo cobro</span><span>Estado</span><span></span>
                  </div>
                  {suscripciones.length === 0
                    ? <div style={s.tablaEmpty}>No hay suscripciones</div>
                    : suscripciones.map(s2 => (
                      <div key={s2.id} style={{ ...s.tablaRow, display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 1fr 180px" }}>
                        <div>
                          <p style={s.tenantNombre}>{s2.tenant_nombre}</p>
                          <p style={s.tenantRnc}>{s2.tenant_email}</p>
                          {s2.facturas_pendientes > 0 && (
                            <span style={{ ...s.badge, background:"#7f1d1d", color:"#fca5a5", fontSize:11 }}>
                              {s2.facturas_pendientes} factura{s2.facturas_pendientes > 1 ? "s" : ""} pendiente{s2.facturas_pendientes > 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                        <span style={{ color:"#94a3b8", fontSize:13 }}>{s2.nombre_plan_snapshot}</span>
                        <span style={{ color:"#94a3b8", fontSize:13, textTransform:"capitalize" }}>{s2.tipo_ciclo}</span>
                        <span style={{ color:"#6ee7b7", fontSize:13, fontWeight:600 }}>
                          {s2.moneda} {parseFloat(s2.precio_acordado).toLocaleString("es-DO",{minimumFractionDigits:2})}
                          {s2.descuento_pct > 0 && <span style={{ color:"#94a3b8", marginLeft:4, fontSize:11 }}>(-{s2.descuento_pct}%)</span>}
                        </span>
                        <span style={{ color: new Date(s2.fecha_proximo_cobro) <= new Date() ? "#f87171" : "#94a3b8", fontSize:13 }}>
                          {s2.fecha_proximo_cobro}
                        </span>
                        <span style={{ ...s.badge, ...estadoBadgeSus(s2.estado) }}>{s2.estado}</span>
                        <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                          <button
                            onClick={() => accionGenerarFactura(s2.tenant_id, s2.tenant_nombre)}
                            disabled={generandoFactura === s2.tenant_id}
                            style={{ ...s.btnMini, fontSize:11, color:"#fcd34d", borderColor:"#f59e0b", opacity: generandoFactura === s2.tenant_id ? 0.5 : 1 }}>
                            {generandoFactura === s2.tenant_id ? "⏳" : "⚡"} Generar
                          </button>
                          <button
                            onClick={() => { setFiltroTenantFac(String(s2.tenant_id)); setFiltroEstadoFac(""); setBillingSubTab("facturas"); cargarFacturas("", String(s2.tenant_id)); }}
                            style={{ ...s.btnMini, fontSize:11 }}>
                            🧾 Facturas
                          </button>
                          <button
                            onClick={() => abrirEditarSus(s2)}
                            style={{ ...s.btnMini, fontSize:11, color:"#93c5fd", borderColor:"#3b82f6" }}>
                            ✏️ Editar
                          </button>
                        </div>
                      </div>
                    ))
                  }
                </div>
              </>
            )}

            {/* ── SUB-TAB: FACTURAS ── */}
            {billingSubTab === "facturas" && (
              <>
                <div style={{ display:"flex", gap:8, marginBottom:16, alignItems:"center", flexWrap:"wrap" }}>
                  <select style={{ ...s.input, width:180 }} value={filtroEstadoFac}
                    onChange={e => { setFiltroEstadoFac(e.target.value); cargarFacturas(e.target.value, filtroTenantFac); }}>
                    <option value="">Todos los estados</option>
                    <option value="pendiente">Pendiente</option>
                    <option value="pagada">Pagada</option>
                    <option value="vencida">Vencida</option>
                    <option value="fallida">Fallida</option>
                    <option value="anulada">Anulada</option>
                  </select>
                  <select style={{ ...s.input, width:200 }} value={filtroTenantFac}
                    onChange={e => { setFiltroTenantFac(e.target.value); cargarFacturas(filtroEstadoFac, e.target.value); }}>
                    <option value="">Todas las empresas</option>
                    {tenants.map(t => <option key={t.id} value={t.id}>#{t.id} {t.nombre}</option>)}
                  </select>
                  {filtroTenantFac && (
                    <button onClick={() => { setFiltroTenantFac(""); cargarFacturas(filtroEstadoFac, ""); }} style={{ ...s.btnMini, color:"#f87171", borderColor:"#f87171" }}>
                      ✕ Quitar filtro empresa
                    </button>
                  )}
                  <button onClick={() => cargarFacturas(filtroEstadoFac, filtroTenantFac)} style={s.btnMini}>↺ Actualizar</button>
                </div>
                <div style={s.tabla}>
                  <div style={{ ...s.tablaHead, gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 100px", display:"grid" }}>
                    <span>Empresa</span><span>Período</span><span>Monto</span><span>Vencimiento</span><span>Estado</span><span></span>
                  </div>
                  {facturas.length === 0
                    ? <div style={s.tablaEmpty}>No hay facturas</div>
                    : facturas.map(f => (
                      <div key={f.id} style={{ ...s.tablaRow, display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 100px" }}>
                        <div>
                          <p style={s.tenantNombre}>{f.tenant_nombre}</p>
                          <p style={s.tenantRnc}>{f.tenant_email}</p>
                        </div>
                        <span style={{ color:"#94a3b8", fontSize:13 }}>{f.periodo}</span>
                        <span style={{ color:"#6ee7b7", fontSize:13, fontWeight:600 }}>
                          {f.moneda} {parseFloat(f.monto).toLocaleString("es-DO",{minimumFractionDigits:2})}
                        </span>
                        <span style={{ color: new Date(f.fecha_vencimiento) < new Date() && f.estado === "pendiente" ? "#f87171" : "#94a3b8", fontSize:13 }}>
                          {f.fecha_vencimiento}
                        </span>
                        <span style={{ ...s.badge, ...estadoBadgeFac(f.estado) }}>{f.estado}</span>
                        <div>
                          {f.estado === "pendiente" && (
                            <button onClick={() => { setModalPagar(f); setFormPago(p => ({ ...p, monto: f.monto })); }}
                              disabled={saving}
                              style={{ ...s.btnMini, color:"#6ee7b7", borderColor:"#6ee7b7", opacity: saving ? 0.5 : 1 }}>
                              💰 Pagar
                            </button>
                          )}
                          {f.estado === "pagada" && <span style={{ color:"#6ee7b7", fontSize:12 }}>✓ Pagada</span>}
                        </div>
                      </div>
                    ))
                  }
                </div>
              </>
            )}

            {/* ── SUB-TAB: PAGOS ── */}
            {billingSubTab === "pagos" && (
              <>
                <div style={{ display:"flex", gap:8, marginBottom:16, alignItems:"center" }}>
                  <button onClick={cargarPagos} style={s.btnMini}>↺ Actualizar</button>
                  <span style={{ color:"#64748b", fontSize:12 }}>{pagos.length} pago{pagos.length !== 1 ? "s" : ""} registrado{pagos.length !== 1 ? "s" : ""}</span>
                </div>
                <div style={s.tabla}>
                  <div style={{ ...s.tablaHead, gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 1fr", display:"grid" }}>
                    <span>Empresa</span><span>Período</span><span>Monto</span><span>Método</span><span>Estado</span><span>Fecha</span>
                  </div>
                  {pagos.length === 0
                    ? <div style={s.tablaEmpty}>No hay pagos registrados</div>
                    : pagos.map(p => (
                      <div key={p.id} style={{ ...s.tablaRow, display:"grid", gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr 1fr" }}>
                        <div>
                          <p style={s.tenantNombre}>{p.tenant_nombre}</p>
                          {p.referencia_gateway && <p style={s.tenantRnc}>Ref: {p.referencia_gateway}</p>}
                        </div>
                        <span style={{ color:"#94a3b8", fontSize:13 }}>{p.periodo}</span>
                        <span style={{ color:"#6ee7b7", fontSize:13, fontWeight:600 }}>
                          {p.moneda} {parseFloat(p.monto).toLocaleString("es-DO",{minimumFractionDigits:2})}
                        </span>
                        <span style={{ color:"#94a3b8", fontSize:13, textTransform:"capitalize" }}>{p.metodo}</span>
                        <span style={{ ...s.badge, ...estadoBadgePago(p.estado) }}>{p.estado}</span>
                        <span style={{ color:"#64748b", fontSize:12 }}>
                          {new Date(p.created_at).toLocaleDateString("es-DO")}
                        </span>
                      </div>
                    ))
                  }
                </div>
              </>
            )}

            {/* ── SUB-TAB: TASAS DE CAMBIO ── */}
            {billingSubTab === "tasas" && (
              <>
                <div style={{ display:"flex", gap:8, marginBottom:16, alignItems:"center" }}>
                  <button onClick={abrirCrearTasa} style={s.btnPrimary}>+ Nueva tasa</button>
                  <button onClick={cargarTasas} style={s.btnMini}>↺ Actualizar</button>
                  <span style={{ color:"#64748b", fontSize:12 }}>
                    {tasas.length} tasa{tasas.length !== 1 ? "s" : ""} registrada{tasas.length !== 1 ? "s" : ""}
                  </span>
                </div>

                <div style={{ background:"#1e293b", border:"1px solid #334155", borderRadius:8, padding:12, marginBottom:16 }}>
                  <p style={{ margin:0, color:"#94a3b8", fontSize:12, lineHeight:1.6 }}>
                    <strong style={{ color:"#e2e8f0" }}>Tasas de cambio para billing multimoneda.</strong>
                    {" "}Al generar una factura en moneda distinta a DOP se usa la tasa más reciente registrada para esa fecha.
                    Si no hay tasa disponible, el sistema rechaza la generación y te pide registrar la tasa primero.
                  </p>
                </div>

                <div style={s.tabla}>
                  <div style={{ ...s.tablaHead, gridTemplateColumns:"1fr 1fr 1.5fr 1.5fr 1fr 1fr", display:"grid" }}>
                    <span>Origen</span><span>Destino</span><span>Tasa</span><span>Fecha vigencia</span><span>Fuente</span><span>Acciones</span>
                  </div>
                  {tasas.length === 0
                    ? <div style={s.tablaEmpty}>No hay tasas registradas. Agrega la primera tasa.</div>
                    : tasas.map(t => (
                      <div key={t.id} style={{ ...s.tablaRow, display:"grid", gridTemplateColumns:"1fr 1fr 1.5fr 1.5fr 1fr 1fr", alignItems:"center" }}>
                        <span style={{ ...s.badge, background:"#1e40af22", color:"#93c5fd", border:"1px solid #1e40af", width:"fit-content", padding:"2px 10px" }}>
                          {t.moneda_origen}
                        </span>
                        <span style={{ ...s.badge, background:"#05966922", color:"#6ee7b7", border:"1px solid #059669", width:"fit-content", padding:"2px 10px" }}>
                          {t.moneda_destino}
                        </span>
                        <span style={{ color:"#f1f5f9", fontWeight:700, fontSize:15, fontVariantNumeric:"tabular-nums" }}>
                          {parseFloat(t.tasa).toLocaleString("es-DO", { minimumFractionDigits:2, maximumFractionDigits:6 })}
                        </span>
                        <span style={{ color:"#94a3b8", fontSize:13 }}>{t.fecha}</span>
                        <span style={{ color:"#64748b", fontSize:12, textTransform:"capitalize" }}>{t.fuente}</span>
                        <div style={{ display:"flex", gap:6 }}>
                          <button onClick={() => abrirEditarTasa(t)} style={s.btnMini}>✏️</button>
                          <button
                            onClick={() => accionEliminarTasa(t.id)}
                            style={{ ...s.btnMini, color:"#f87171", borderColor:"#991b1b" }}
                            disabled={eliminandoTasa === t.id}
                          >
                            {eliminandoTasa === t.id ? "..." : "🗑️"}
                          </button>
                        </div>
                      </div>
                    ))
                  }
                </div>
              </>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════════
            TAB: AUDIT LOG
        ════════════════════════════════════════════════════════ */}
        {tab === "audit" && (
          <div>
            <div style={s.tabHeader}>
              <h2 style={s.tabTitle}>Audit Log Global <span style={{color:"#64748b", fontSize:14}}>({totalAudit} eventos)</span></h2>
              <button onClick={cargarAudit} style={s.btnSecondary}>🔄 Actualizar</button>
            </div>

            <div style={s.tabla}>
              <div style={s.tablaHead}>
                <span style={{flex:1.5}}>Fecha</span>
                <span style={{flex:1}}>Empresa</span>
                <span style={{flex:1}}>Usuario</span>
                <span style={{flex:2}}>Acción</span>
                <span style={{flex:1}}>Recurso</span>
                <span style={{flex:1}}>IP</span>
              </div>

              {audit.length === 0 && <div style={s.tablaEmpty}>No hay eventos registrados</div>}

              {audit.map(log => (
                <div key={log.id} style={{...s.tablaRow, fontSize:13}}>
                  <span style={{flex:1.5, color:"#64748b"}}>{formatFecha(log.timestamp)}</span>
                  <span style={{flex:1, color:"#94a3b8"}}>{log.tenant_nombre || "Global"}</span>
                  <span style={{flex:1, color:"#94a3b8"}}>{log.usuario_nombre || "—"}</span>
                  <span style={{flex:2, color:"#a5b4fc", fontWeight:600}}>{log.accion}</span>
                  <span style={{flex:1, color:"#64748b"}}>{log.recurso}</span>
                  <span style={{flex:1, color:"#475569"}}>{log.ip || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>

      {/* ════════════════════════════════════════════════════════════
          MODAL: EDITAR EMPRESA
      ════════════════════════════════════════════════════════════ */}
      {modalEditar && (
        <Modal titulo={`✏️ Editar Empresa — #${modalEditar.id}`} onClose={cerrarEditarTenant} ancho={680}>

          {/* ── Paso 1: Formulario ─────────────────────────────── */}
          {!confirmCambio && (
            <>
              <div style={s.formGrid}>
                <FormField label="Nombre de la empresa *">
                  <input style={s.input} value={formEditar.nombre}
                    onChange={e => setFormEditar(p => ({ ...p, nombre: e.target.value }))}
                    placeholder="Nombre de la empresa" />
                </FormField>
                <FormField label="Estado">
                  <select style={s.input} value={formEditar.estado}
                    onChange={e => setFormEditar(p => ({ ...p, estado: e.target.value }))}>
                    <option value="activo">✅ Activo</option>
                    <option value="suspendido">⛔ Suspendido</option>
                    <option value="pendiente">⏳ Pendiente</option>
                  </select>
                </FormField>
                <FormField label="Plan asignado">
                  <select style={s.input} value={formEditar.plan_id}
                    onChange={e => onChangePlanEditar(e.target.value)}>
                    <option value="">— Sin plan —</option>
                    {planes.map(p => (
                      <option key={p.id} value={String(p.id)}>
                        {p.nombre} (${p.precio_mensual}/mes)
                      </option>
                    ))}
                  </select>
                </FormField>
                <div style={{ display:'flex', alignItems:'flex-end', paddingBottom:4 }}>
                  <span style={{ fontSize:12, color:'#475569' }}>
                    Plan actual: <strong style={{color:'#a5b4fc'}}>{modalEditar.plan_nombre || '—'}</strong>
                  </span>
                </div>
                {suscripcionEditar && (
                  <div style={{ gridColumn:'1 / -1' }}>
                    <FormField label="Método de pago (suscripción)">
                      <select style={s.input} value={formEditar.metodo_pago || "manual"}
                        onChange={e => setFormEditar(p => ({ ...p, metodo_pago: e.target.value }))}>
                        <option value="manual">Manual</option>
                        <option value="transferencia">🏦 Transferencia</option>
                        <option value="stripe">💳 Stripe</option>
                        <option value="azul">🔵 Azul</option>
                      </select>
                      {formEditar.metodo_pago === "stripe" && suscripcionEditar.metodo_pago !== "stripe" && (
                        <span style={{ fontSize:11, color:"#a5b4fc", marginTop:4, display:"block" }}>
                          ℹ️ Se creará Customer en Stripe automáticamente si no existe
                        </span>
                      )}
                    </FormField>
                  </div>
                )}
              </div>

              {/* ── Sección Add-ons de pago ─────────────────────── */}
              <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #1e293b' }}>
                <p style={{ margin: '0 0 12px', color: '#a5b4fc', fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  💎 Add-ons de pago
                </p>

                {loadingAddons ? (
                  <div style={{ color: '#64748b', fontSize: 13, padding: '8px 0' }}>⏳ Cargando add-ons...</div>
                ) : (
                  <div style={{ border: '1px solid #334155', borderRadius: 10, overflow: 'hidden' }}>
                    {/* Cabecera tabla */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 160px', padding: '8px 14px', background: '#0f172a', borderBottom: '1px solid #334155', fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      <span>Add-on</span>
                      <span style={{ textAlign: 'center' }}>Activo</span>
                      <span style={{ textAlign: 'center' }}>Facturar</span>
                      <span style={{ textAlign: 'center' }}>Precio (DOP)</span>
                    </div>

                    {ADDONS_PAGO.map((def, idx) => {
                      const asignado = addonsTenant.asignados.find(a => a.codigo === def.codigo && a.estado === 'activo');
                      const activo   = !!asignado;
                      const esSaving = savingAddon === def.codigo;
                      const precioVal = preciosAddon[def.codigo] !== undefined
                        ? preciosAddon[def.codigo]
                        : String(asignado?.precio_acordado ?? def.precio_ref);

                      return (
                        <div key={def.codigo} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 160px', padding: '10px 14px', borderBottom: idx < ADDONS_PAGO.length - 1 ? '1px solid #1e293b' : 'none', alignItems: 'center', background: activo ? 'rgba(99,102,241,0.06)' : 'transparent' }}>

                          {/* Nombre + precio referencia */}
                          <div>
                            <span style={{ fontSize: 13, fontWeight: 600, color: activo ? '#f1f5f9' : '#64748b' }}>
                              {def.icono} {def.nombre}
                            </span>
                            <span style={{ display: 'block', fontSize: 11, color: '#475569', marginTop: 2 }}>
                              Ref: RD${def.precio_ref}/mes
                            </span>
                          </div>

                          {/* Toggle Activo */}
                          <div style={{ textAlign: 'center' }}>
                            <button
                              disabled={esSaving}
                              onClick={() => toggleAddonTenant(def.codigo, def.nombre, !activo)}
                              style={{ padding: '4px 10px', borderRadius: 20, border: 'none', cursor: esSaving ? 'wait' : 'pointer', fontSize: 12, fontWeight: 700, background: activo ? '#064e3b' : '#1e293b', color: activo ? '#6ee7b7' : '#64748b', minWidth: 54, transition: 'all 0.15s' }}>
                              {esSaving ? '⏳' : (activo ? '✅ ON' : '○ OFF')}
                            </button>
                          </div>

                          {/* Toggle Facturar */}
                          <div style={{ textAlign: 'center' }}>
                            <button
                              disabled={!activo || esSaving}
                              onClick={() => toggleFacturarAddon(def.codigo, def.nombre, !asignado?.facturar)}
                              style={{ padding: '4px 10px', borderRadius: 20, border: 'none', cursor: (!activo || esSaving) ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 700, background: !activo ? '#0f172a' : (asignado?.facturar ? '#422006' : '#1e293b'), color: !activo ? '#334155' : (asignado?.facturar ? '#fb923c' : '#64748b'), minWidth: 54, transition: 'all 0.15s', opacity: !activo ? 0.4 : 1 }}>
                              {asignado?.facturar ? '💰 SÍ' : '— NO'}
                            </button>
                          </div>

                          {/* Precio acordado editable */}
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'center' }}>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              disabled={!activo || esSaving}
                              value={precioVal}
                              onChange={e => setPreciosAddon(prev => ({ ...prev, [def.codigo]: e.target.value }))}
                              style={{ padding: '5px 8px', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#f1f5f9', fontSize: 13, width: 80, boxSizing: 'border-box', outline: 'none', opacity: !activo ? 0.35 : 1 }}
                            />
                            {activo && String(precioVal) !== String(asignado?.precio_acordado ?? def.precio_ref) && (
                              <button
                                disabled={esSaving}
                                onClick={() => guardarPrecioAddon(def.codigo, def.nombre)}
                                title="Guardar precio"
                                style={{ padding: '5px 7px', background: '#6366f1', border: 'none', borderRadius: 6, color: 'white', fontSize: 12, cursor: esSaving ? 'wait' : 'pointer' }}>
                                💾
                              </button>
                            )}
                          </div>

                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Preview del cambio de plan */}
              {loadingPreview && (
                <div style={{ padding:'12px 0', color:'#94a3b8', fontSize:13 }}>⏳ Calculando impacto del cambio de plan...</div>
              )}

              {previewPlan && !loadingPreview && (
                <div style={{ background:'#0f172a', border:'1px solid #334155', borderRadius:10, padding:16, marginBottom:16 }}>
                  <p style={{ margin:'0 0 12px', fontWeight:700, color:'#f1f5f9', fontSize:14 }}>
                    📋 Impacto del cambio: <span style={{color:'#94a3b8'}}>{previewPlan.plan_actual.nombre}</span>
                    {' → '}
                    <span style={{color:'#a5b4fc'}}>{previewPlan.plan_nuevo.nombre}</span>
                  </p>

                  {previewPlan.a_activar.length > 0 && (
                    <div style={{ marginBottom:10 }}>
                      <p style={{ margin:'0 0 6px', fontSize:12, color:'#6ee7b7', fontWeight:600 }}>
                        ✅ Se ACTIVARÁN ({previewPlan.a_activar.length}):
                      </p>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                        {previewPlan.a_activar.map(m => (
                          <span key={m} style={{ ...s.badge, background:'#064e3b', color:'#6ee7b7' }}>{m}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {previewPlan.a_desactivar.length > 0 && (
                    <div style={{ marginBottom:10 }}>
                      <p style={{ margin:'0 0 6px', fontSize:12, color:'#fca5a5', fontWeight:600 }}>
                        ⛔ Se DESACTIVARÁN ({previewPlan.a_desactivar.length}):
                      </p>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                        {previewPlan.a_desactivar.map(m => (
                          <span key={m} style={{ ...s.badge, background:'#7f1d1d', color:'#fca5a5' }}>{m}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {previewPlan.manuales_mantenidos.length > 0 && (
                    <div>
                      <p style={{ margin:'0 0 6px', fontSize:12, color:'#fcd34d', fontWeight:600 }}>
                        🔒 Se MANTIENEN (activados manualmente, {previewPlan.manuales_mantenidos.length}):
                      </p>
                      <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                        {previewPlan.manuales_mantenidos.map(m => (
                          <span key={m} style={{ ...s.badge, background:'#78350f', color:'#fcd34d' }}>{m}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {previewPlan.a_activar.length === 0 && previewPlan.a_desactivar.length === 0 && (
                    <p style={{ color:'#64748b', fontSize:13, margin:0 }}>Sin cambios en módulos.</p>
                  )}
                </div>
              )}

              <div style={s.modalFooter}>
                <button onClick={cerrarEditarTenant} style={s.btnSecondary}>Cancelar</button>
                <button onClick={guardarEdicionTenant} style={s.btnPrimary} disabled={saving || loadingPreview}>
                  {saving ? "Guardando..." : (formEditar.plan_id && formEditar.plan_id !== String(modalEditar.plan_id) ? "Siguiente →" : "✅ Guardar cambios")}
                </button>
              </div>
            </>
          )}

          {/* ── Paso 2: Confirmación de cambio de plan ─────────── */}
          {confirmCambio && previewPlan && (
            <>
              <div style={{ background:'#1e293b', border:'1px solid #f59e0b', borderRadius:10, padding:20, marginBottom:20 }}>
                <p style={{ margin:'0 0 16px', fontWeight:700, color:'#fcd34d', fontSize:15 }}>
                  ⚠️ Confirmar cambio de plan
                </p>
                <p style={{ color:'#94a3b8', fontSize:14, margin:'0 0 16px' }}>
                  Estás a punto de cambiar el plan de <strong style={{color:'#f1f5f9'}}>{modalEditar.nombre}</strong>:
                  <br />
                  <span style={{color:'#94a3b8'}}>{previewPlan.plan_actual.nombre}</span>
                  {' → '}
                  <strong style={{color:'#a5b4fc'}}>{previewPlan.plan_nuevo.nombre}</strong>
                </p>

                <div style={{ display:'flex', gap:32 }}>
                  <div>
                    <p style={{ margin:'0 0 6px', fontSize:12, color:'#6ee7b7', fontWeight:600 }}>
                      ✅ Activarán: {previewPlan.a_activar.length} módulo{previewPlan.a_activar.length !== 1 ? 's' : ''}
                    </p>
                    {previewPlan.a_activar.map(m => <div key={m} style={{color:'#6ee7b7', fontSize:13}}>• {m}</div>)}
                  </div>
                  <div>
                    <p style={{ margin:'0 0 6px', fontSize:12, color:'#fca5a5', fontWeight:600 }}>
                      ⛔ Desactivarán: {previewPlan.a_desactivar.length} módulo{previewPlan.a_desactivar.length !== 1 ? 's' : ''}
                    </p>
                    {previewPlan.a_desactivar.map(m => <div key={m} style={{color:'#fca5a5', fontSize:13}}>• {m}</div>)}
                  </div>
                </div>
              </div>

              <div style={s.modalFooter}>
                <button onClick={() => setConfirmCambio(false)} style={s.btnSecondary}>← Volver</button>
                <button onClick={guardarEdicionTenant} style={{...s.btnPrimary, background:'#f59e0b', color:'#000'}} disabled={saving}>
                  {saving ? "Aplicando..." : "✅ Confirmar y aplicar"}
                </button>
              </div>
            </>
          )}

        </Modal>
      )}

      {/* ════════════════════════════════════════════════════════════
          MODAL: CREAR EMPRESA
      ════════════════════════════════════════════════════════════ */}
      {modalTenant && (
        <Modal titulo="🏢 Nueva Empresa" onClose={() => setModalTenant(false)}>
          <div style={s.formGrid}>
            <FormField label="Nombre de la empresa *">
              <input style={s.input} value={formTenant.nombre} onChange={e => setFormTenant({...formTenant, nombre: e.target.value})} placeholder="Ej: Empresa XYZ" />
            </FormField>
            <FormField label="RNC">
              <input style={s.input} value={formTenant.rnc} onChange={e => setFormTenant({...formTenant, rnc: e.target.value})} placeholder="131-XXXXXX-X" />
            </FormField>
            <FormField label="Email">
              <input style={s.input} value={formTenant.email} onChange={e => setFormTenant({...formTenant, email: e.target.value})} placeholder="admin@empresa.com" />
            </FormField>
            <FormField label="Teléfono">
              <input style={s.input} value={formTenant.telefono} onChange={e => setFormTenant({...formTenant, telefono: e.target.value})} placeholder="809-000-0000" />
            </FormField>
            <FormField label="Plan *">
              <select style={s.input} value={formTenant.plan_id} onChange={e => setFormTenant({...formTenant, plan_id: e.target.value})}>
                <option value="">— Seleccionar plan —</option>
                {planes.map(p => <option key={p.id} value={p.id}>{p.nombre} (${p.precio_mensual}/mes)</option>)}
              </select>
            </FormField>
            <FormField label="Trial">
              <label style={{display:'flex', alignItems:'center', gap:8, color:'#94a3b8', cursor:'pointer', marginBottom:8}}>
                <input type="checkbox" checked={formTenant.es_trial} onChange={e => setFormTenant({...formTenant, es_trial: e.target.checked, dias_trial: e.target.checked ? 15 : 0})} />
                Cuenta Trial
              </label>
              {formTenant.es_trial && (
                <input
                  style={s.input}
                  type="number" min="1" max="365"
                  value={formTenant.dias_trial}
                  onChange={e => setFormTenant({...formTenant, dias_trial: parseInt(e.target.value) || 15})}
                  placeholder="Días de trial (default 15)"
                />
              )}
            </FormField>
          </div>

          {/* ── Administrador ─────────────────────────────── */}
          <div style={{ margin: "20px 0 8px", paddingTop: 20, borderTop: "1px solid #334155" }}>
            <p style={{ margin: "0 0 14px", color: "#a5b4fc", fontSize: 13, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase" }}>
              👤 Usuario administrador
            </p>
            <div style={s.formGrid}>
              <FormField label="Nombre del administrador *">
                <input style={s.input} value={formTenant.nombre_admin} onChange={e => setFormTenant({...formTenant, nombre_admin: e.target.value})} placeholder="Ej: Juan Pérez" />
              </FormField>
              <FormField label="Email del administrador *">
                <input style={s.input} type="email" value={formTenant.email_admin} onChange={e => setFormTenant({...formTenant, email_admin: e.target.value})} placeholder="admin@empresa.com" />
              </FormField>
              <FormField label="Contraseña temporal *">
                <input style={s.input} type="text" value={formTenant.password_admin} onChange={e => setFormTenant({...formTenant, password_admin: e.target.value})} placeholder="Contraseña inicial" />
              </FormField>
            </div>
          </div>

          <div style={s.modalFooter}>
            <button onClick={() => setModalTenant(false)} style={s.btnSecondary}>Cancelar</button>
            <button onClick={crearTenant} style={s.btnPrimary} disabled={saving}>{saving ? "Creando..." : "✅ Crear Empresa"}
          </button>
          </div>
        </Modal>
      )}

      {/* ════════════════════════════════════════════════════════════
          MODAL: CREAR PLAN
      ════════════════════════════════════════════════════════════ */}
      {modalPlan && (
        <Modal titulo={planEditando ? `✏️ Editar Plan — ${planEditando.nombre}` : "📦 Nuevo Plan"} onClose={() => { setModalPlan(false); setPlanEditando(null); setFormPlan({ nombre: "", descripcion: "", precio_mensual: "", modulos_incluidos: [] }); }}>
          <div style={s.formGrid}>
            <FormField label="Nombre del plan *">
              <input style={s.input} value={formPlan.nombre} onChange={e => setFormPlan({...formPlan, nombre: e.target.value})} placeholder="Ej: Básico, Pro, Enterprise" />
            </FormField>
            <FormField label="Precio mensual (USD) *">
              <input style={s.input} type="number" value={formPlan.precio_mensual} onChange={e => setFormPlan({...formPlan, precio_mensual: e.target.value})} placeholder="99.00" />
            </FormField>
            <FormField label="Descripción">
              <input style={s.input} value={formPlan.descripcion} onChange={e => setFormPlan({...formPlan, descripcion: e.target.value})} placeholder="Descripción del plan" />
            </FormField>
          </div>
          <FormField label="Módulos incluidos">
            <div style={s.modulosGrid}>
              {MODULOS_DISPONIBLES.map(m => (
                <label key={m} style={{...s.moduloCheck, ...(formPlan.modulos_incluidos.includes(m) ? s.moduloCheckActive : {})}}>
                  <input type="checkbox" style={{display:'none'}} checked={formPlan.modulos_incluidos.includes(m)} onChange={() => toggleModuloPlan(m)} />
                  {m}
                </label>
              ))}
            </div>
          </FormField>
          <div style={s.modalFooter}>
            <button onClick={() => { setModalPlan(false); setPlanEditando(null); setFormPlan({ nombre: "", descripcion: "", precio_mensual: "", modulos_incluidos: [] }); }} style={s.btnSecondary}>Cancelar</button>
            <button onClick={crearPlan} style={s.btnPrimary} disabled={saving}>{saving ? "Guardando..." : planEditando ? "✅ Guardar cambios" : "✅ Crear Plan"}</button>
          </div>
        </Modal>
      )}

      {/* ════════════════════════════════════════════════════════════
          MODAL: GESTIONAR MÓDULOS DE EMPRESA
      ════════════════════════════════════════════════════════════ */}
      {modalModulos && (
        <Modal titulo={`⚙️ Módulos — ${modalModulos.nombre}`} onClose={() => setModalModulos(null)} ancho={560}>
          <p style={{color:"#64748b", fontSize:13, marginBottom:12}}>
            Plan: <strong style={{color:"#a5b4fc"}}>{modalModulos.plan_nombre}</strong> ·
            Estado: <strong style={{color:"#6ee7b7"}}>{modalModulos.estado}</strong>
          </p>

          {/* Selector de país del tenant */}
          <div style={{ marginBottom:20, padding:"12px 16px", background:"#0f172a", borderRadius:10, border:"1px solid #334155" }}>
            <label style={{ fontSize:13, fontWeight:600, color:"#94a3b8", display:"block", marginBottom:8 }}>
              🌍 País del tenant
            </label>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <select
                value={modalModulos.pais_codigo || "DO"}
                onChange={async (e) => {
                  const nuevo = e.target.value;
                  try {
                    await api.patch(`/superadmin/tenants/${modalModulos.id}/pais`, { pais_codigo: nuevo });
                    setModalModulos(prev => ({...prev, pais_codigo: nuevo}));
                    mostrarMensaje(`✅ País actualizado a ${nuevo}`, "success");
                  } catch { mostrarMensaje("❌ Error al cambiar país", "error"); }
                }}
                style={{...s.input, flex:1}}
              >
                {paises.length === 0
                  ? <option value="DO">DO — República Dominicana</option>
                  : paises.filter(p => p.activo).map(p => (
                      <option key={p.codigo} value={p.codigo}>
                        {p.codigo} — {p.nombre} ({p.impuesto_label} {p.impuesto_tasa_default}% · {p.moneda_simbolo} {p.moneda_codigo})
                      </option>
                    ))
                }
              </select>
            </div>
          </div>

          {/* Override límite empresas contables */}
          <div style={{ marginBottom:20, padding:"12px 16px", background:"#0f172a", borderRadius:10, border:"1px solid #334155" }}>
            <label style={{ fontSize:13, fontWeight:600, color:"#94a3b8", display:"block", marginBottom:8 }}>
              🏢 Límite empresas contables
            </label>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <input
                type="number"
                min="1"
                placeholder={`Según plan (${modalModulos.plan_nombre})`}
                value={modalModulos.max_empresas_override ?? ""}
                onChange={e => setModalModulos(prev => ({
                  ...prev,
                  max_empresas_override: e.target.value === "" ? null : parseInt(e.target.value)
                }))}
                style={{...s.input, flex:1, width:"100px"}}
              />
              <button
                onClick={async () => {
                  try {
                    await api.patch(`/superadmin/tenants/${modalModulos.id}/limite-empresas`, {
                      max_empresas_override: modalModulos.max_empresas_override
                    });
                    mostrarMensaje("✅ Límite actualizado", "success");
                  } catch { mostrarMensaje("❌ Error al actualizar límite", "error"); }
                }}
                style={s.btnPrimary}
              >
                Guardar
              </button>
            </div>
            <small style={{ color:"#475569", marginTop:6, display:"block" }}>
              Dejar vacío para usar el límite del plan. Usar -1 para ilimitado.
            </small>
          </div>

          <p style={{color:"#64748b", fontSize:12, marginBottom:12}}>Módulos activos:</p>
          <div style={s.modulosGrid}>
            {MODULOS_DISPONIBLES.map(m => {
              const modData = modalModulos.modulos?.find(x => x.modulo === m);
              const activo = modData?.activo || false;
              return (
                <div key={m} onClick={() => toggleModulo(modalModulos.id, m, !activo)}
                  style={{...s.moduloCheck, ...(activo ? s.moduloCheckActive : {}), cursor:'pointer', userSelect:'none'}}>
                  <span>{activo ? "✅" : "⬜"}</span> {m}
                </div>
              );
            })}
          </div>
          <div style={s.modalFooter}>
            <button onClick={() => setModalModulos(null)} style={s.btnPrimary}>Cerrar</button>
          </div>
        </Modal>
      )}

        {/* ════════════════════════════════════════════════════════
            TAB: PAÍSES / MULTI-PAÍS
        ════════════════════════════════════════════════════════ */}
        {tab === "paises" && (
          <div>
            <div style={s.tabHeader}>
              <div>
                <h2 style={s.tabTitle}>Gestión de Países</h2>
                <p style={{ color: "#94a3b8", fontSize: 13, margin: 0 }}>
                  Configura los parámetros fiscales por país. Los tenants eligen su país al registrarse.
                </p>
              </div>
              <button onClick={() => abrirModalPais()} style={s.btnPrimary}>+ Nuevo País</button>
            </div>

            <div style={s.tabla}>
              <div style={{...s.tablaHead, gridTemplateColumns: "none"}}>
                <div style={{ display:"flex", gap:0, width:"100%" }}>
                  <span style={{flex:0.4}}>Código</span>
                  <span style={{flex:2}}>País</span>
                  <span style={{flex:1}}>Moneda</span>
                  <span style={{flex:1}}>Impuesto</span>
                  <span style={{flex:1}}>ID Fiscal</span>
                  <span style={{flex:1}}>Sistema</span>
                  <span style={{flex:1}}>Regulador</span>
                  <span style={{flex:0.8}}>Estado</span>
                  <span style={{flex:1}}>Acciones</span>
                </div>
              </div>

              {paises.length === 0 && (
                <div style={{ padding: 32, textAlign: "center", color: "#64748b" }}>Cargando países...</div>
              )}

              {paises.map(p => (
                <div key={p.codigo} style={{ ...s.tablaRow, display:"flex", alignItems:"center" }}>
                  <span style={{flex:0.4}}>
                    <span style={{ ...s.badge, background:"#1e293b", color:"#6366f1", fontSize:13, fontWeight:700 }}>{p.codigo}</span>
                  </span>
                  <span style={{flex:2}}>
                    <strong style={{ color:"#f1f5f9" }}>{p.nombre}</strong>
                    {p.zona_horaria && <div style={{ fontSize:11, color:"#64748b" }}>{p.zona_horaria}</div>}
                  </span>
                  <span style={{flex:1, color:"#94a3b8"}}>
                    <strong>{p.moneda_simbolo}</strong> {p.moneda_codigo}
                  </span>
                  <span style={{flex:1}}>
                    <span style={{ ...s.badge, background:"#14532d", color:"#86efac" }}>
                      {p.impuesto_label} {p.impuesto_tasa_default}%
                    </span>
                  </span>
                  <span style={{flex:1, color:"#94a3b8", fontSize:13}}>{p.id_fiscal_empresa_label}</span>
                  <span style={{flex:1}}>
                    {p.sistema_comprobantes && (
                      <span style={{ ...s.badge, background:"#1e3a5f", color:"#93c5fd", fontSize:12 }}>
                        {p.sistema_comprobantes}
                      </span>
                    )}
                  </span>
                  <span style={{flex:1, color:"#94a3b8", fontSize:13}}>{p.ente_regulador_nombre || "—"}</span>
                  <span style={{flex:0.8}}>
                    <span style={{ ...s.badge, ...(p.activo ? {background:"#064e3b",color:"#6ee7b7"} : {background:"#7f1d1d",color:"#fca5a5"}) }}>
                      {p.activo ? "Activo" : "Inactivo"}
                    </span>
                  </span>
                  <span style={{flex:1, display:"flex", gap:6}}>
                    <button onClick={() => abrirModalPais(p)} style={{...s.btnMini, color:"#93c5fd"}}>✏️ Editar</button>
                    <button
                      onClick={() => toggleActivoPais(p.codigo, !p.activo)}
                      style={{...s.btnMini, color: p.activo ? "#fca5a5" : "#6ee7b7"}}
                    >
                      {p.activo ? "⛔ Desact." : "✅ Activ."}
                    </button>
                  </span>
                </div>
              ))}
            </div>

            {/* Info de uso */}
            <div style={{ marginTop:20, padding:16, background:"#0f172a", borderRadius:10, border:"1px solid #334155" }}>
              <p style={{ color:"#64748b", fontSize:13, margin:0 }}>
                💡 <strong style={{color:"#94a3b8"}}>Para agregar soporte completo a un país:</strong> crea el registro aquí (parámetros fiscales básicos) y luego crea el archivo <code style={{color:"#6366f1"}}>XX.js</code> en <code style={{color:"#6366f1"}}>backend/config/paises/</code> para la lógica de facturación electrónica.
              </p>
            </div>
          </div>
        )}

        {/* ── MODAL CREAR/EDITAR PAÍS ──────────────────────────────── */}
        {modalPais && (
          <Modal
            titulo={paisEditando ? `✏️ Editar País — ${paisEditando.codigo}` : "🌍 Nuevo País"}
            onClose={() => setModalPais(false)}
            ancho={760}
          >
            <div style={s.formGrid}>
              <FormField label="Código ISO (2 letras) *">
                <input style={{...s.input, textTransform:"uppercase"}} value={formPais.codigo}
                  onChange={e => setFormPais(p=>({...p, codigo: e.target.value.toUpperCase().slice(0,2)}))}
                  placeholder="CO, MX, GT..." disabled={!!paisEditando} maxLength={2} />
              </FormField>
              <FormField label="Nombre del País *">
                <input style={s.input} value={formPais.nombre}
                  onChange={e => setFormPais(p=>({...p, nombre:e.target.value}))}
                  placeholder="Colombia" />
              </FormField>
              <FormField label="Código Moneda (ISO 4217)">
                <input style={s.input} value={formPais.moneda_codigo}
                  onChange={e => setFormPais(p=>({...p, moneda_codigo:e.target.value.toUpperCase().slice(0,3)}))}
                  placeholder="COP, MXN, USD..." maxLength={3} />
              </FormField>
              <FormField label="Símbolo Moneda">
                <input style={s.input} value={formPais.moneda_simbolo}
                  onChange={e => setFormPais(p=>({...p, moneda_simbolo:e.target.value.slice(0,5)}))}
                  placeholder="COP$, MX$, $..." maxLength={5} />
              </FormField>
              <FormField label="Nombre Moneda">
                <input style={s.input} value={formPais.moneda_nombre}
                  onChange={e => setFormPais(p=>({...p, moneda_nombre:e.target.value}))}
                  placeholder="Peso Colombiano" />
              </FormField>
              <FormField label="Zona Horaria">
                <input style={s.input} value={formPais.zona_horaria}
                  onChange={e => setFormPais(p=>({...p, zona_horaria:e.target.value}))}
                  placeholder="America/Bogota" />
              </FormField>
              <FormField label="Nombre del Impuesto (IVA/ITBIS...) *">
                <input style={s.input} value={formPais.impuesto_label}
                  onChange={e => setFormPais(p=>({...p, impuesto_label:e.target.value}))}
                  placeholder="IVA, ITBIS, GST..." />
              </FormField>
              <FormField label="Tasa Default del Impuesto (%) *">
                <input style={s.input} type="number" value={formPais.impuesto_tasa_default}
                  onChange={e => setFormPais(p=>({...p, impuesto_tasa_default:e.target.value}))}
                  placeholder="19, 18, 16..." />
              </FormField>
              <FormField label="Label ID Fiscal (Empresa)">
                <input style={s.input} value={formPais.id_fiscal_empresa_label}
                  onChange={e => setFormPais(p=>({...p, id_fiscal_empresa_label:e.target.value}))}
                  placeholder="NIT, RFC, RUC..." />
              </FormField>
              <FormField label="Label ID Fiscal (Persona)">
                <input style={s.input} value={formPais.id_fiscal_persona_label}
                  onChange={e => setFormPais(p=>({...p, id_fiscal_persona_label:e.target.value}))}
                  placeholder="Cédula de Ciudadanía..." />
              </FormField>
              <FormField label="Sistema de Comprobantes">
                <input style={s.input} value={formPais.sistema_comprobantes}
                  onChange={e => setFormPais(p=>({...p, sistema_comprobantes:e.target.value}))}
                  placeholder="CUFE, CFDI, NCF, FEL..." />
              </FormField>
              <FormField label="Ente Regulador">
                <input style={s.input} value={formPais.ente_regulador_nombre}
                  onChange={e => setFormPais(p=>({...p, ente_regulador_nombre:e.target.value}))}
                  placeholder="DIAN, SAT, DGII..." />
              </FormField>
              <FormField label="URL Ente Regulador">
                <input style={s.input} value={formPais.ente_regulador_url}
                  onChange={e => setFormPais(p=>({...p, ente_regulador_url:e.target.value}))}
                  placeholder="https://www.dian.gov.co" />
              </FormField>
              <FormField label="Orden (para el selector)">
                <input style={s.input} type="number" value={formPais.orden}
                  onChange={e => setFormPais(p=>({...p, orden:parseInt(e.target.value)||99}))}
                  placeholder="1, 2, 3..." />
              </FormField>
            </div>
            <div style={{ display:"flex", gap:12, alignItems:"center", marginBottom:16 }}>
              <label style={{ display:"flex", gap:8, alignItems:"center", color:"#94a3b8", fontSize:14, cursor:"pointer" }}>
                <input type="checkbox" checked={formPais.activo}
                  onChange={e => setFormPais(p=>({...p, activo:e.target.checked}))} />
                País activo (visible para los tenants)
              </label>
              <label style={{ display:"flex", gap:8, alignItems:"center", color:"#94a3b8", fontSize:14, cursor:"pointer" }}>
                <input type="checkbox" checked={formPais.comprobantes_activo}
                  onChange={e => setFormPais(p=>({...p, comprobantes_activo:e.target.checked}))} />
                Comprobantes fiscales activos
              </label>
            </div>
            <div style={s.modalFooter}>
              <button onClick={() => setModalPais(false)} style={s.btnSecondary}>Cancelar</button>
              <button onClick={guardarPais} style={s.btnPrimary} disabled={saving}>
                {saving ? "Guardando..." : (paisEditando ? "Actualizar País" : "Crear País")}
              </button>
            </div>
          </Modal>
        )}

      {/* ════════════════════════════════════════════════════════════
          MODAL: EXTENDER TRIAL
      ════════════════════════════════════════════════════════════ */}
      {modalExtenderTrial && (
        <Modal titulo={`⏳ Extender Trial — ${modalExtenderTrial.nombre}`} onClose={() => setModalExtenderTrial(null)} ancho={420}>
          <p style={{color:'#94a3b8', fontSize:14, marginBottom:16}}>
            Días restantes actuales: <strong style={{color:'#fcd34d'}}>{modalExtenderTrial.dias_trial_restantes}</strong>
            {modalExtenderTrial.fecha_expiracion && <> · Vence: <strong style={{color:'#fcd34d'}}>{new Date(modalExtenderTrial.fecha_expiracion + 'T00:00:00').toLocaleDateString('es-DO')}</strong></>}
          </p>
          <FormField label="Días extra a agregar">
            <input style={s.input} type="number" min="1" max="365"
              value={diasExtra} onChange={e => setDiasExtra(parseInt(e.target.value) || 1)} />
          </FormField>
          <div style={s.modalFooter}>
            <button onClick={() => setModalExtenderTrial(null)} style={s.btnSecondary}>Cancelar</button>
            <button onClick={guardarExtenderTrial} style={{...s.btnPrimary, background:'#f59e0b', color:'#000'}} disabled={saving}>
              {saving ? "Guardando..." : `✅ Agregar ${diasExtra} días`}
            </button>
          </div>
        </Modal>
      )}

      {/* ════════════════════════════════════════════════════════════
          MODAL: CONVERTIR TRIAL A PLAN
      ════════════════════════════════════════════════════════════ */}
      {modalConvertir && (
        <Modal titulo={`💳 Convertir a Plan — ${modalConvertir.nombre}`} onClose={() => setModalConvertir(null)} ancho={460}>
          <p style={{color:'#94a3b8', fontSize:14, marginBottom:16}}>
            Selecciona el plan a asignar. Se desactivará el trial y se activarán los módulos del plan.
          </p>
          <FormField label="Plan a asignar *">
            <select style={s.input} value={planConvertir} onChange={e => setPlanConvertir(e.target.value)}>
              <option value="">— Seleccionar plan —</option>
              {planes.map(p => <option key={p.id} value={p.id}>{p.nombre} (${p.precio_mensual}/mes)</option>)}
            </select>
          </FormField>
          <div style={s.modalFooter}>
            <button onClick={() => setModalConvertir(null)} style={s.btnSecondary}>Cancelar</button>
            <button onClick={guardarConvertirPlan} style={{...s.btnPrimary, background:'#059669'}} disabled={saving}>
              {saving ? "Convirtiendo..." : "✅ Convertir a Plan Pagado"}
            </button>
          </div>
        </Modal>
      )}

      {/* ════════════════════════════════════════════════════════════
          MODAL: CREAR USUARIO
      ════════════════════════════════════════════════════════════ */}
      {modalUsuario && (
        <Modal titulo="👤 Nuevo Usuario" onClose={() => setModalUsuario(false)} ancho={520}>
          <div style={s.formGrid}>
            <FormField label="Nombre completo *">
              <input style={s.input} value={formUser.nombre}
                onChange={e => setFormUser(p => ({ ...p, nombre: e.target.value }))}
                placeholder="Juan Pérez" />
            </FormField>
            <FormField label="Email *">
              <input style={s.input} type="email" value={formUser.email}
                onChange={e => setFormUser(p => ({ ...p, email: e.target.value }))}
                placeholder="juan@empresa.com" />
            </FormField>
            <FormField label="Contraseña *">
              <input style={s.input} type="text" value={formUser.password}
                onChange={e => setFormUser(p => ({ ...p, password: e.target.value }))}
                placeholder="Contraseña inicial" />
            </FormField>
            <FormField label="Empresa *" style={{ gridColumn: "1 / -1" }}>
              <select style={s.input} value={formUser.tenant_id}
                onChange={e => setFormUser(p => ({ ...p, tenant_id: e.target.value }))}>
                <option value="">— Seleccionar empresa —</option>
                {tenants.map(t => <option key={t.id} value={t.id}>#{t.id} {t.nombre} ({t.plan_nombre || 'sin plan'})</option>)}
              </select>
            </FormField>
            <FormField label="Rol *">
              <select style={s.input} value={formUser.rol}
                onChange={e => setFormUser(p => ({ ...p, rol: e.target.value }))}>
                <optgroup label="— Roles Generales —">
                  {ROLES_USUARIO.map(r => <option key={r} value={r}>{r}</option>)}
                </optgroup>
                <optgroup label="— Roles Contables —">
                  {ROLES_CONTABLES.map(r => <option key={r} value={r}>{r}</option>)}
                </optgroup>
              </select>
            </FormField>
          </div>
          <div style={s.modalFooter}>
            <button onClick={() => setModalUsuario(false)} style={s.btnSecondary}>Cancelar</button>
            <button onClick={accionCrearUsuario} style={s.btnPrimary} disabled={saving}>
              {saving ? "Creando..." : "✅ Crear Usuario"}
            </button>
          </div>
        </Modal>
      )}

      {/* ════════════════════════════════════════════════════════════
          MODAL: EDITAR USUARIO
      ════════════════════════════════════════════════════════════ */}
      {modalEditarUser && (
        <Modal titulo={`✏️ Editar Usuario — ${modalEditarUser.nombre}`} onClose={() => { setModalEditarUser(null); setFormUser(FORM_USER_DEFAULT); }} ancho={520}>
          <div style={s.formGrid}>
            <FormField label="Nombre completo">
              <input style={s.input} value={formUser.nombre}
                onChange={e => setFormUser(p => ({ ...p, nombre: e.target.value }))}
                placeholder="Juan Pérez" />
            </FormField>
            <FormField label="Email">
              <input style={s.input} type="email" value={formUser.email}
                onChange={e => setFormUser(p => ({ ...p, email: e.target.value }))}
                placeholder="juan@empresa.com" />
            </FormField>
            <FormField label="Nueva contraseña (vacío = no cambiar)">
              <input style={s.input} type="text" value={formUser.password}
                onChange={e => setFormUser(p => ({ ...p, password: e.target.value }))}
                placeholder="Dejar vacío para no cambiar" />
            </FormField>
            <FormField label="Rol">
              <select style={s.input} value={formUser.rol}
                onChange={e => setFormUser(p => ({ ...p, rol: e.target.value }))}>
                <optgroup label="— Roles Generales —">
                  {ROLES_USUARIO.map(r => <option key={r} value={r}>{r}</option>)}
                </optgroup>
                <optgroup label="— Roles Contables —">
                  {ROLES_CONTABLES.map(r => <option key={r} value={r}>{r}</option>)}
                </optgroup>
              </select>
            </FormField>
            <FormField label="Empresa">
              <select style={s.input} value={formUser.tenant_id}
                onChange={e => setFormUser(p => ({ ...p, tenant_id: e.target.value }))}>
                <option value="">— Sin empresa (huérfano) —</option>
                {tenants.map(t => <option key={t.id} value={t.id}>#{t.id} {t.nombre} ({t.plan_nombre || 'sin plan'})</option>)}
              </select>
            </FormField>
            <FormField label="Estado">
              <select style={s.input} value={formUser.activo ? "1" : "0"}
                onChange={e => setFormUser(p => ({ ...p, activo: e.target.value === "1" }))}>
                <option value="1">✅ Activo</option>
                <option value="0">⛔ Inactivo</option>
              </select>
            </FormField>
          </div>
          <div style={s.modalFooter}>
            <button onClick={() => { setModalEditarUser(null); setFormUser(FORM_USER_DEFAULT); }} style={s.btnSecondary}>Cancelar</button>
            <button onClick={accionEditarUsuario} style={s.btnPrimary} disabled={saving}>
              {saving ? "Guardando..." : "✅ Guardar cambios"}
            </button>
          </div>
        </Modal>
      )}

      {/* ── MODAL CREAR SUSCRIPCIÓN ────────────────────────────── */}
      {modalCrearSus && (
        <Modal titulo="+ Nueva Suscripción" onClose={() => setModalCrearSus(false)} ancho={560}>
          <div style={s.formGrid}>
            <FormField label="Empresa *">
              <select style={s.input} value={formSus.tenant_id}
                onChange={e => setFormSus(p => ({ ...p, tenant_id: e.target.value }))}>
                <option value="">— Seleccionar empresa —</option>
                {tenants.map(t => <option key={t.id} value={t.id}>#{t.id} {t.nombre}</option>)}
              </select>
            </FormField>
            <FormField label="Plan *">
              <select style={s.input} value={formSus.plan_id}
                onChange={e => {
                  const p = planes.find(p => p.id === parseInt(e.target.value));
                  setFormSus(prev => ({ ...prev, plan_id: e.target.value, precio_acordado: p ? p.precio_mensual : prev.precio_acordado }));
                }}>
                <option value="">— Seleccionar plan —</option>
                {planes.map(p => <option key={p.id} value={p.id}>{p.nombre} — {p.precio_mensual}/mes</option>)}
              </select>
            </FormField>
            <FormField label="Ciclo">
              <select style={s.input} value={formSus.tipo_ciclo}
                onChange={e => setFormSus(p => ({ ...p, tipo_ciclo: e.target.value }))}>
                <option value="mensual">Mensual</option>
                <option value="anual">Anual</option>
              </select>
            </FormField>
            <FormField label="Método de pago">
              <select style={s.input} value={formSus.metodo_pago}
                onChange={e => setFormSus(p => ({ ...p, metodo_pago: e.target.value }))}>
                <option value="manual">Manual</option>
                <option value="transferencia">Transferencia</option>
                <option value="azul">Azul</option>
                <option value="stripe">Stripe</option>
              </select>
            </FormField>
            <FormField label="Precio acordado *">
              <input style={s.input} type="number" min="0" step="0.01"
                value={formSus.precio_acordado}
                onChange={e => setFormSus(p => ({ ...p, precio_acordado: e.target.value }))}
                placeholder="0.00" />
            </FormField>
            <FormField label="Descuento %">
              <input style={s.input} type="number" min="0" max="100" step="0.01"
                value={formSus.descuento_pct}
                onChange={e => setFormSus(p => ({ ...p, descuento_pct: e.target.value }))}
                placeholder="0" />
            </FormField>
            <FormField label="Moneda">
              <select style={s.input} value={formSus.moneda}
                onChange={e => setFormSus(p => ({ ...p, moneda: e.target.value }))}>
                <option value="DOP">DOP</option>
                <option value="USD">USD</option>
              </select>
            </FormField>
          </div>
          <FormField label="Notas internas">
            <input style={s.input} value={formSus.notas}
              onChange={e => setFormSus(p => ({ ...p, notas: e.target.value }))}
              placeholder="Observaciones opcionales" />
          </FormField>
          <div style={s.modalFooter}>
            <button onClick={() => setModalCrearSus(false)} style={s.btnSecondary}>Cancelar</button>
            <button onClick={accionCrearSuscripcion} style={s.btnPrimary} disabled={saving}>
              {saving ? "Guardando..." : "✅ Crear suscripción"}
            </button>
          </div>
        </Modal>
      )}

      {/* ── MODAL EDITAR SUSCRIPCIÓN ──────────────────────────── */}
      {modalEditarSus && (
        <Modal titulo={`✏️ Editar Suscripción — ${modalEditarSus.tenant_nombre}`} onClose={() => setModalEditarSus(null)} ancho={560}>
          <div style={s.formGrid}>
            <FormField label="Método de pago">
              <select style={s.input} value={formEditarSus.metodo_pago}
                onChange={e => setFormEditarSus(p => ({ ...p, metodo_pago: e.target.value }))}>
                <option value="manual">Manual</option>
                <option value="transferencia">🏦 Transferencia</option>
                <option value="stripe">💳 Stripe</option>
                <option value="azul">🔵 Azul</option>
              </select>
              {formEditarSus.metodo_pago === "stripe" && modalEditarSus.metodo_pago !== "stripe" && (
                <span style={{ fontSize:11, color:"#a5b4fc", marginTop:4, display:"block" }}>
                  ℹ️ Se creará Customer en Stripe si no existe
                </span>
              )}
            </FormField>
            <FormField label="Ciclo">
              <select style={s.input} value={formEditarSus.tipo_ciclo}
                onChange={e => setFormEditarSus(p => ({ ...p, tipo_ciclo: e.target.value }))}>
                <option value="mensual">Mensual</option>
                <option value="anual">Anual</option>
              </select>
            </FormField>
            <FormField label="Precio acordado *">
              <input style={s.input} type="number" min="0" step="0.01"
                value={formEditarSus.precio_acordado}
                onChange={e => setFormEditarSus(p => ({ ...p, precio_acordado: e.target.value }))}
                placeholder="0.00" />
            </FormField>
            <FormField label="Descuento %">
              <input style={s.input} type="number" min="0" max="100" step="0.01"
                value={formEditarSus.descuento_pct}
                onChange={e => setFormEditarSus(p => ({ ...p, descuento_pct: e.target.value }))}
                placeholder="0" />
            </FormField>
            <FormField label="Moneda">
              <select style={s.input} value={formEditarSus.moneda}
                onChange={e => setFormEditarSus(p => ({ ...p, moneda: e.target.value }))}>
                <option value="DOP">DOP</option>
                <option value="USD">USD</option>
              </select>
            </FormField>
          </div>
          <FormField label="Notas internas">
            <input style={s.input} value={formEditarSus.notas}
              onChange={e => setFormEditarSus(p => ({ ...p, notas: e.target.value }))}
              placeholder="Observaciones opcionales" />
          </FormField>
          <div style={s.modalFooter}>
            <button onClick={() => setModalEditarSus(null)} style={s.btnSecondary}>Cancelar</button>
            <button onClick={guardarEdicionSus} style={s.btnPrimary} disabled={saving}>
              {saving ? "Guardando..." : "✅ Guardar cambios"}
            </button>
          </div>
        </Modal>
      )}

      {/* ── MODAL REGISTRAR PAGO ───────────────────────────────── */}
      {modalPagar && (
        <Modal titulo={`💰 Registrar Pago — ${modalPagar.tenant_nombre} (${modalPagar.periodo})`} onClose={() => setModalPagar(null)} ancho={480}>
          <div style={{ background:"#0f172a", borderRadius:8, padding:14, marginBottom:16 }}>
            <p style={{ margin:0, color:"#64748b", fontSize:13 }}>Factura #{modalPagar.id} · Período {modalPagar.periodo}</p>
            <p style={{ margin:"6px 0 0", color:"#6ee7b7", fontSize:20, fontWeight:700 }}>
              {modalPagar.moneda} {parseFloat(modalPagar.monto).toLocaleString("es-DO",{minimumFractionDigits:2})}
            </p>
            {new Date(modalPagar.fecha_vencimiento) < new Date() && (
              <p style={{ margin:"6px 0 0", color:"#f87171", fontSize:12 }}>⚠️ Vencida el {modalPagar.fecha_vencimiento}</p>
            )}
          </div>
          <div style={s.formGrid}>
            <FormField label="Monto recibido *">
              <input style={s.input} type="number" min="0" step="0.01"
                value={formPago.monto}
                onChange={e => setFormPago(p => ({ ...p, monto: e.target.value }))} />
            </FormField>
            <FormField label="Moneda">
              <select style={s.input} value={formPago.moneda}
                onChange={e => setFormPago(p => ({ ...p, moneda: e.target.value }))}>
                <option value="DOP">DOP</option>
                <option value="USD">USD</option>
              </select>
            </FormField>
            <FormField label="Método">
              <select style={s.input} value={formPago.metodo}
                onChange={e => setFormPago(p => ({ ...p, metodo: e.target.value }))}>
                <option value="manual">Manual</option>
                <option value="transferencia">Transferencia</option>
                <option value="efectivo">Efectivo</option>
                <option value="azul">Azul</option>
                <option value="stripe">Stripe</option>
              </select>
            </FormField>
            <FormField label="Referencia de pago">
              <input style={s.input} value={formPago.referencia_pago}
                onChange={e => setFormPago(p => ({ ...p, referencia_pago: e.target.value }))}
                placeholder="Nro. transacción o comprobante" />
            </FormField>
          </div>
          <p style={{ color:"#64748b", fontSize:12, margin:"0 0 12px" }}>
            Al registrar el pago el tenant será reactivado automáticamente si estaba suspendido o en modo solo lectura.
          </p>
          <div style={s.modalFooter}>
            <button onClick={() => setModalPagar(null)} style={s.btnSecondary}>Cancelar</button>
            <button onClick={accionRegistrarPago} style={s.btnPrimary} disabled={saving}>
              {saving ? "Guardando..." : "✅ Confirmar pago"}
            </button>
          </div>
        </Modal>
      )}

      {/* ── MODAL CREAR / EDITAR TASA DE CAMBIO ───────────────── */}
      {modalTasa && (
        <Modal
          titulo={modalTasa === "crear" ? "💱 Nueva Tasa de Cambio" : `✏️ Editar Tasa — ${modalTasa.moneda_origen}→${modalTasa.moneda_destino} (${modalTasa.fecha})`}
          onClose={() => setModalTasa(null)}
          ancho={440}
        >
          <div style={s.formGrid}>
            <FormField label="Moneda origen *">
              <select style={s.input} value={formTasa.moneda_origen}
                onChange={e => setFormTasa(p => ({ ...p, moneda_origen: e.target.value }))}
                disabled={modalTasa !== "crear"}>
                {["USD","EUR","GBP","CAD","MXN","COP","PEN","CLP","ARS","BRL"].map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Moneda destino *">
              <select style={s.input} value={formTasa.moneda_destino}
                onChange={e => setFormTasa(p => ({ ...p, moneda_destino: e.target.value }))}
                disabled={modalTasa !== "crear"}>
                {["DOP","USD","EUR"].map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Tasa (1 origen = X destino) *">
              <input style={s.input} type="number" min="0.000001" step="0.01"
                value={formTasa.tasa}
                onChange={e => setFormTasa(p => ({ ...p, tasa: e.target.value }))}
                placeholder="ej. 60.50" />
            </FormField>
            <FormField label="Fecha de vigencia *">
              <input style={s.input} type="date"
                value={formTasa.fecha}
                onChange={e => setFormTasa(p => ({ ...p, fecha: e.target.value }))}
                disabled={modalTasa !== "crear"} />
            </FormField>
            <FormField label="Fuente">
              <select style={s.input} value={formTasa.fuente}
                onChange={e => setFormTasa(p => ({ ...p, fuente: e.target.value }))}>
                <option value="manual">Manual</option>
                <option value="bcrd">BCRD</option>
                <option value="api">API automática</option>
              </select>
            </FormField>
          </div>
          {formTasa.tasa && formTasa.moneda_origen && (
            <div style={{ background:"#0f172a", border:"1px solid #334155", borderRadius:6, padding:"10px 14px", marginBottom:12 }}>
              <p style={{ margin:0, color:"#94a3b8", fontSize:12 }}>
                Equivalencia: <strong style={{ color:"#6ee7b7" }}>
                  1 {formTasa.moneda_origen} = {parseFloat(formTasa.tasa || 0).toLocaleString("es-DO",{minimumFractionDigits:2,maximumFractionDigits:6})} {formTasa.moneda_destino}
                </strong>
              </p>
            </div>
          )}
          <div style={s.modalFooter}>
            <button onClick={() => setModalTasa(null)} style={s.btnSecondary}>Cancelar</button>
            <button onClick={accionGuardarTasa} style={s.btnPrimary} disabled={saving}>
              {saving ? "Guardando..." : (modalTasa === "crear" ? "✅ Registrar tasa" : "✅ Guardar cambios")}
            </button>
          </div>
        </Modal>
      )}

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
};

// ── COMPONENTES AUXILIARES ────────────────────────────────────────

const StatCard = ({ label, valor, color, icon }) => (
  <div style={s.statCard}>
    <span style={{fontSize:32}}>{icon}</span>
    <p style={{...s.statValor, color}}>{valor ?? "—"}</p>
    <p style={s.statLabel}>{label}</p>
  </div>
);

const Modal = ({ titulo, onClose, children, ancho = 620 }) => (
  <div style={s.modalOverlay}>
    <div style={{...s.modalBox, maxWidth: ancho}}>
      <div style={s.modalHeader}>
        <h2 style={s.modalTitulo}>{titulo}</h2>
        <button onClick={onClose} style={s.btnCerrarModal}>✕</button>
      </div>
      <div style={s.modalBody}>{children}</div>
    </div>
  </div>
);

const FormField = ({ label, children }) => (
  <div style={s.formField}>
    {label && <label style={s.formLabel}>{label}</label>}
    {children}
  </div>
);

// ════════════════════════════════════════════════════════════════════
// ESTILOS — TEMA OSCURO PROFESIONAL
// ════════════════════════════════════════════════════════════════════
const s = {
  root:         { minHeight:"100vh", background:"#0f172a", color:"#e2e8f0", fontFamily:"'Segoe UI', sans-serif" },
  loadingWrap:  { minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", background:"#0f172a", gap:16 },
  spinner:      { width:36, height:36, border:"3px solid #1e293b", borderTopColor:"#6366f1", borderRadius:"50%", animation:"spin 0.8s linear infinite" },

  // Header
  header:       { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"20px 32px", background:"#1e293b", borderBottom:"1px solid #334155" },
  headerLeft:   { display:"flex", alignItems:"center", gap:16 },
  crown:        { fontSize:40 },
  headerTitle:  { margin:0, fontSize:22, fontWeight:700, color:"#f1f5f9" },
  headerSub:    { margin:"4px 0 0", fontSize:13, color:"#64748b" },
  btnVolver:    { padding:"8px 16px", background:"transparent", border:"1px solid #334155", color:"#94a3b8", borderRadius:8, cursor:"pointer", fontSize:13 },

  // Mensaje global
  mensajeGlobal:{ margin:"0 32px", padding:"12px 20px", borderRadius:8, border:"1px solid", fontSize:14, fontWeight:600, marginTop:16 },

  // Tabs
  tabBar:       { display:"flex", gap:4, padding:"16px 32px", background:"#0f172a", borderBottom:"1px solid #1e293b" },
  tabBtn:       { padding:"10px 20px", background:"transparent", border:"1px solid #1e293b", color:"#64748b", borderRadius:8, cursor:"pointer", fontSize:14, fontWeight:500, transition:"all 0.2s" },
  tabBtnActive: { background:"#6366f1", borderColor:"#6366f1", color:"white" },

  // Content
  content:      { padding:"24px 32px" },
  tabHeader:    { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 },
  tabTitle:     { margin:0, fontSize:18, fontWeight:700, color:"#f1f5f9" },
  sectionTitle: { color:"#94a3b8", fontSize:14, fontWeight:600, textTransform:"uppercase", letterSpacing:1, margin:"24px 0 12px" },

  // Stats
  statsGrid:    { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(180px, 1fr))", gap:16 },
  statCard:     { background:"#1e293b", borderRadius:12, padding:"20px", textAlign:"center", border:"1px solid #334155" },
  statValor:    { fontSize:36, fontWeight:800, margin:"8px 0 4px" },
  statLabel:    { color:"#64748b", fontSize:13, margin:0 },

  // Planes stats
  planesGrid:   { display:"flex", gap:12, flexWrap:"wrap" },
  planStatCard: { background:"#1e293b", border:"1px solid #334155", borderRadius:10, padding:"16px 24px", textAlign:"center", minWidth:120 },
  planStatNombre:{ margin:0, color:"#94a3b8", fontSize:13 },
  planStatTotal: { margin:"8px 0 2px", fontSize:28, fontWeight:800, color:"#a5b4fc" },
  planStatLabel: { margin:0, color:"#475569", fontSize:12 },

  // Tabla
  tabla:        { background:"#1e293b", borderRadius:12, border:"1px solid #334155", overflow:"hidden" },
  tablaHead:    { display:"flex", padding:"12px 16px", background:"#0f172a", borderBottom:"1px solid #334155", fontSize:12, fontWeight:600, color:"#64748b", textTransform:"uppercase", letterSpacing:0.5, gap:8 },
  tablaRow:     { display:"flex", alignItems:"center", padding:"14px 16px", borderBottom:"1px solid #1e293b", gap:8, transition:"background 0.15s" },
  tablaEmpty:   { padding:"32px", textAlign:"center", color:"#475569" },
  tenantNombre: { margin:0, fontWeight:600, color:"#f1f5f9", fontSize:14 },
  tenantRnc:    { margin:"2px 0 0", fontSize:12, color:"#64748b" },

  // Planes cards
  planesCards:   { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:16 },
  planCard:      { background:"#1e293b", border:"1px solid #334155", borderRadius:12, padding:20 },
  planCardHeader:{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 },
  planCardNombre:{ margin:0, fontSize:18, fontWeight:700, color:"#f1f5f9" },
  planCardPrecio:{ fontSize:22, fontWeight:800, color:"#6ee7b7" },
  planCardDesc:  { color:"#64748b", fontSize:13, margin:"0 0 12px" },
  planCardModulos:{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:12 },
  planCardFooter:{ display:"flex", justifyContent:"space-between", alignItems:"center", marginTop:12, paddingTop:12, borderTop:"1px solid #334155" },

  // Botones
  btnPrimary:   { padding:"10px 20px", background:"#6366f1", color:"white", border:"none", borderRadius:8, cursor:"pointer", fontSize:14, fontWeight:600 },
  btnSecondary: { padding:"10px 20px", background:"transparent", color:"#94a3b8", border:"1px solid #334155", borderRadius:8, cursor:"pointer", fontSize:14 },
  btnMini:      { padding:"5px 10px", background:"transparent", color:"#94a3b8", border:"1px solid #334155", borderRadius:6, cursor:"pointer", fontSize:12 },

  // Badges
  badge:        { padding:"3px 10px", borderRadius:20, fontSize:12, fontWeight:600 },
  moduloBadge:  { padding:"4px 10px", background:"#1e3a5f", color:"#93c5fd", borderRadius:20, fontSize:12 },

  // Modal
  modalOverlay: { position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16 },
  modalBox:     { background:"#1e293b", borderRadius:16, width:"100%", border:"1px solid #334155", maxHeight:"90vh", overflow:"auto" },
  modalHeader:  { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"20px 24px", borderBottom:"1px solid #334155" },
  modalTitulo:  { margin:0, fontSize:18, fontWeight:700, color:"#f1f5f9" },
  modalBody:    { padding:"20px 24px" },
  modalFooter:  { display:"flex", justifyContent:"flex-end", gap:8, marginTop:20, paddingTop:16, borderTop:"1px solid #334155" },
  btnCerrarModal:{ background:"transparent", border:"none", color:"#64748b", fontSize:20, cursor:"pointer" },

  // Form
  formGrid:     { display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 },
  formField:    { display:"flex", flexDirection:"column", gap:6 },
  formLabel:    { fontSize:13, fontWeight:600, color:"#94a3b8" },
  input:        { padding:"10px 12px", background:"#0f172a", border:"1px solid #334155", borderRadius:8, color:"#f1f5f9", fontSize:14, outline:"none", width:"100%", boxSizing:"border-box" },

  // Módulos selector
  modulosGrid:  { display:"flex", flexWrap:"wrap", gap:8, marginTop:8 },
  moduloCheck:  { padding:"8px 14px", background:"#0f172a", border:"1px solid #334155", borderRadius:8, color:"#64748b", fontSize:13, cursor:"pointer", display:"flex", alignItems:"center", gap:6 },
  moduloCheckActive: { background:"#1e3a5f", border:"1px solid #3b82f6", color:"#93c5fd" },
};

export default SuperAdminPanel;